'use client';

import { commandLinkUnconfirmed, hasNoCommandLink } from '@/lib/companion-command-link';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocket } from '@/hooks/useWebSocket';
import { api, ApiRequestError, getApiErrorMessage } from '@/lib/api';
import { missionStopFailure, missionStopMessage, type MissionStopMessage } from '@/lib/mission-stop-outcome';
import { isCurrentlyFlying } from '@/lib/drone-telemetry';
import { TopBar } from '@/components/dashboard/TopBar';
import { QuickDispatchDialog } from '@/components/dashboard/QuickDispatchDialog';
import { useSiteDroneFilter } from './_cop/useSiteDroneFilter';
import { zoneReducer, initialZoneState } from './_cop/zoneAuthoring';
import { ZonesPanel, type SaveGeofenceData } from '@/components/dashboard/ZonesPanel';
import { INITIAL_ZONE_FORM, zoneFormReducer } from '@/components/dashboard/zones/zoneForm';
import { resolveZoneColor } from '@/components/dashboard/zones/zoneColors';
import { useCopContextData } from './_cop/useCopContextData';
import { useDroneRegistry } from './_cop/useDroneRegistry';
import { dispatchableDrones } from './_cop/dispatchableDrones';
import { useDroneCommand } from '@/hooks/useDroneCommand';
import { useManualControl, type ManualControlController } from '@/hooks/useManualControl';
import { useCopAlerts } from './_cop/useCopAlerts';
import { useDockMissionPlan } from '@/hooks/useDockMissionPlan';
import { useDockBays, useDockOperationalPhase } from '@/hooks/useDockDetail';
import { DockLaunchOverlay } from '@/components/dashboard/DockLaunchOverlay';
import type { LaunchMode } from '@/components/dashboard/DockBayRow';
import type { LiveDrone } from '@/hooks/useWebSocket';
import {
  activeWaypointIndex,
  extractPolygonRing,
  pointInPolygonRing,
  type KeepInFence,
  type LatLng,
} from '@/lib/mission-planning';
import { resolveDockPosition } from '@/lib/dock-position';
import { commandRouteOf, commandSecretPresentOf } from '@/lib/drone-command-availability';
import type { DockMarker, PlanRoutePoint, TriggerMarker } from '@/components/dashboard/DroneMap';
import { isLaunchRequestTimeout, LAUNCH_TIMEOUT_MESSAGE } from '@/lib/launch-request';
import type {
  FloatingWindowPosition,
  FloatingWindowSize,
} from '@/components/dashboard/FloatingWindow';
import type { HlsStreamState } from '@/components/dashboard/HlsVideo';
import type { DroneSelectSource } from '@/components/dashboard/droneMapTypes';
import type { DockCameraSlot } from '@/lib/dock-camera';
import type { Dock, LiveDockStatus } from '@/types/dock';
import {
  selectionReducer,
  initialSelection,
  dispatchReducer,
  initialDispatch,
  isDockPlanEditable,
  SINGLE_CAMERA,
  cameraWindowKey,
  cameraWindowName,
  cameraWindowsFull,
  dropTransientWindows,
  freeCascadeSlot,
  poppedCamerasFor,
  sameCameraWindow,
  MAX_CAMERA_WINDOWS,
} from './_cop/copReducers';
import type { CameraWindow, SelectionAction } from './_cop/copReducers';
import {
  pinLaunchedMission,
  resolveDroneMission,
  type LaunchedMissions,
} from './_cop/missionBinding';
import { CopAlertBell } from './_cop/CopAlertBell';
import { copSystemStatus } from '@/lib/system-status';
import { CopMapStage } from './_cop/CopMapStage';
import { useLookHere, type LookHereCameraInputs } from './_cop/useLookHere';
import { CopDroneSearch } from './_cop/CopDroneSearch';
import { DroneSelectionChip } from '@/components/dashboard/DroneSelectionChip';
import { DroneCommandBar } from './_cop/DroneCommandBar';
import { GotoAltitudeBar } from './_cop/GotoAltitudeBar';
import {
  ManualFlightBanner,
  ManualRefusalNote,
  ManualStatusAnnouncer,
} from './_cop/ManualFlightBanner';
import { ManualFlyToHereBar, ManualFlyToOutcomeNote } from './_cop/ManualFlyToHereBar';
import {
  MANUAL_FLY_TO_LATE_CONFIRMED,
  dispatchManualFlyTo,
  watchFlyToAck,
} from './_cop/manualFlyTo';
import { useManualFlyToOutcome } from './_cop/useManualFlyToOutcome';
import { useManualControlSnapshot } from './_cop/useManualControlSnapshot';
import { ManualMapControl, type ManualFlyTarget } from './_cop/ManualMapControl';
import { ManualStage } from './_cop/ManualStage';
import { ManualExitConfirm } from './_cop/ManualExitConfirm';
import { leavesFlownAircraft } from './_cop/manualExitGuard';
import { ManualVideoStage } from './_cop/ManualVideoStage';
import { useManualMapLock } from './_cop/useManualMapLock';
import { useManualStageLayout } from '@/hooks/useManualStageLayout';
import { useManualRingVisibility } from '@/hooks/useManualRingVisibility';
import { useSplitVideoPreference } from '@/hooks/useSplitVideoPreference';
import { useManualDevice, useManualHandedness } from '@/hooks/useManualDevice';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock';
import { useMeasuredSize } from '@/hooks/useMeasuredSize';
import { StageInsetsProvider, useStageInsetsController } from './_cop/useStageInsets';
import { FeedAnalyticsClaim, FeedAnalyticsProvider } from './_cop/useFeedAnalytics';
import { useDashboardShell } from '@/components/dashboard/dashboard-shell-context';
import { CopBottomStack } from './_cop/CopBottomStack';
import { CopTopStack } from './_cop/CopTopStack';
import { ringGeometry } from '@/lib/manual-vector';
import { aircraftYawAvailability, manualControlOffer, manualHoldRefusal } from '@/lib/drone-command-availability';
import { useAircraftAim } from './_cop/useAircraftAim';
import { DroneWorkspace } from './_cop/DroneWorkspace';
import { CopCameraWindows } from './_cop/CopCameraWindows';
import { CameraFailureAnnouncer } from './_cop/CameraFailureAnnouncer';
import type { CameraControlInputs } from './_cop/CameraControlOverlay';
import type { CameraLens } from '@/types/fleet';
import { CopDockSheet } from './_cop/CopDockSheet';
import { CopSidePanel } from './_cop/CopSidePanel';
import { useSidePanelWidth } from './_cop/useSidePanelWidth';
import { CopDockRoster, type DockRosterView } from './_cop/CopDockRoster';
import { CopMissionsPanel } from './_cop/CopMissionsPanel';
import { reliefLinkStatus } from './missions/_components/coveragePresentation';
import { CopMissionCompose } from './_cop/CopMissionCompose';
import { CopMissionEdit, drawnRouteSignature, type EditingMission } from './_cop/CopMissionEdit';
import { useDrawnRoute, type DrawnRouteController } from './_cop/useDrawnRoute';
import {
  CANVAS_ALT_DEFAULT_M,
  MAX_ROUTE_WAYPOINTS,
  toDraftWaypoints,
  type DraftWaypoint,
} from './missions/_components/missionCanvasShared';
import { isRouteEditable } from './missions/_components/missionDetailShared';
import { CopSheetTabs, type CopSheetTab } from './_cop/CopSheetTabs';
import { useCopMissionsQueue } from './_cop/useCopMissionsQueue';
import { useCopLaunchProgress } from './_cop/useCopLaunchProgress';
import { handoverLinksFrom, useCopCoverage } from './_cop/useCopCoverage';
import { useCopRotation } from './_cop/useCopRotation';
import { useVoidedApprovalToasts } from './missions/_components/useVoidedApprovalToasts';
import { GotoReliefQuestionBar } from './_cop/GotoReliefQuestionBar';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { useCoverageCapability } from './_cop/useCoverageCapability';
import { CopCoverageAttachSheet } from './_cop/CopCoverageAttachSheet';
import { useTriggerEvents } from './_cop/useTriggerEvents';
import type { ActiveMission } from './_cop/types';
import { missionDetailHref, type MissionListItem } from './missions/_components/missionListShared';
import type { Drone, Pilot, Site } from '@/types/fleet';
import { useAltitudeProfile } from '@/hooks/useAltitudeProfile';
import { AIR_PICTURE_TITLE, SHEET_TAB } from '@/lib/copy';
import type { TagLandingsByDrone } from '@/lib/tag-landing';
import { ALTITUDE_HARD_BOUNDS, GOTO_MIN_ALTITUDE_M, type AltitudeUnit } from '@/lib/altitude';
import {
  IDLE_MAP_MODE,
  isMapModeArmed,
  bannerDrawnCount,
  standDownEffects,
  mapModeBannerLabel,
  mapModeReducer,
  zoneDrawShape,
  type MapMode,
} from '@/lib/map-mode';
import {
  NO_MEASUREMENTS,
  addMeasurePoint,
  finishMeasureLine,
  openLinePointCount,
  removeMeasureLine,
  type RangeBearingMeasurements,
} from '@/lib/range-bearing';
import {
  geometryShapeForType,
  waypointCountRangeForType,
  DEFAULT_MISSION_TYPE,
  type MissionType,
} from '@/lib/mission-type';

/**
 * Registry key for the navigation rail's claim on the stage's left edge.
 *
 * A literal rather than a `useId`, because the component that registers it owns
 * the registry and there is exactly one rail.
 */
const RAIL_INSET_ID = 'cop-navigation-rail';

/** How many of this console's own manual session ids to keep. See its ref. */
const OWN_MANUAL_SESSION_MEMORY = 32;

/** What STOP needs to know about a mission to name it in every message it produces. */
type StoppableMission = Pick<MissionListItem, 'id' | 'name' | 'drone_callsign'>;


/** Row shape of GET /api/missions/:id — just the fields the route preview needs. */
interface MissionDetailRow {
  id: string;
  name: string;
  status: string;
  /** 022. NOT NULL server-side with a DEFAULT, so every row read back carries one. */
  type: MissionType;
  /**
   * 022/#700. A DRAFT is not in `/api/missions/active`, so this response is the ONLY source of
   * map features for one - and without these the ring cannot be drawn for it, which left a draft
   * orbit as the lone centre dot the ring was added to replace.
   */
  orbit_radius_m?: number | null;
  orbit_direction?: 'cw' | 'ccw' | null;
  assigned_drone_id: string | null;
  drone_callsign?: string | null;
  waypoints?: ActiveMission['waypoints'];
}

function CopView() {
  const searchParams = useSearchParams();
  const siteId = searchParams.get('site_id');
  const { organization, role, user, features } = useAuth();
  const canDispatch = role === 'admin' || role === 'operator';
  // Only an admin can write org settings (PUT /api/org is admin-only), so the
  // "set as default" affordance is withheld from everyone else rather than
  // offered and then rejected with a 403.
  const altitude = useAltitudeProfile();
  const canAckResolve = role === 'admin' || role === 'operator';
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  const {
    connected,
    drones,
    droneMap,
    cooperativeTraffic,
    subscribeFullAlert,
    dockMap,
    companionMap,
    manualControlMap,
    subscribeDockAck,
    subscribeDockEvent,
    subscribeDroneAck,
    subscribeManualControlState,
    subscribeMissionEvent,
    subscribeLaunchEvent,
    subscribeCoverageEvent,
    subscribeRotationEvent,
    subscribePersonDetected,
    subscribeTriggerResolved,
    subscribeTriggerProposal,
  } = useWebSocket(token);
  const router = useRouter();

  const [selection, dispatchSelection] = useReducer(selectionReducer, initialSelection);
  const [quickDispatch, dispatchQuick] = useReducer(dispatchReducer, initialDispatch);

  const allowedDroneIds = useSiteDroneFilter(siteId);
  const {
    geofences,
    sites,
    activeMissions,
    missionsCountFailed,
    maintenanceCountFailed,
    maintenancePredictions,
    geofencesFailed,
    sitesFailed,
    loadGeofences,
    loadMissions,
  } = useCopContextData();

  /** Keyed by drone id, so it is bounded by the fleet and a second launch on the
   *  same airframe replaces rather than accumulates (see missionBinding.ts). */
  const [launchedMissions, setLaunchedMissions] = useState<LaunchedMissions>(() => new Map());

  /** One handler for every launch path the COP owns — dock overlay and quick
   *  dispatch alike. Pins the sortie, then refreshes so it is actually there to
   *  be found; both endpoints set the mission `in_progress` before they answer,
   *  so this refetch cannot arrive ahead of the row it is going to look for. */
  const handleMissionLaunched = useCallback(
    (droneId: string | null, missionId: string | null) => {
      // No aircraft to key on means no pin is possible, but a launch still
      // happened — refresh regardless, or the panel stays blank for the sortie.
      if (droneId) setLaunchedMissions((prev) => pinLaunchedMission(prev, droneId, missionId));
      loadMissions();
    },
    [loadMissions],
  );
  const { alerts, alertFeedState, alertsUpdatedAt, handleAcknowledge, handleResolve } = useCopAlerts({
    subscribeFullAlert,
    canAckResolve,
  });

  // ——— Mission queue (missions panel) ————————————————————————————————————
  const missionsQueue = useCopMissionsQueue(subscribeMissionEvent);

  // ——— Dock launches in flight (#418) ————————————————————————————————————
  // A docked launch answers 202 once the bay is open and finishes without anyone holding the
  // request, so the phases after that - the home-point hold, then the dispatch - reach the
  // operator here rather than on the response body.
  useCopLaunchProgress(subscribeLaunchEvent, droneMap);

  // ——— External triggers (person detection -> a point on the map) ————————
  // Enabled for EVERY role, not just dispatchers. Seeing a detection and acting
  // on one are different permissions: `GET /api/triggers` carries no
  // requireRole for exactly this reason, and a supervisor on a viewer account
  // watching an incident needs the point on the picture to tell an operator
  // where to look. The RBAC gate stays where it belongs, on the two ACTIONS
  // below, which is what TriggerPointCard's dismiss-only mode was written for.
  const {
    triggers, markDispatched, dismiss: dismissTrigger,
    approve: approveTrigger, reject: rejectTrigger,
  } = useTriggerEvents(
    true,
    subscribePersonDetected,
    subscribeTriggerResolved,
    subscribeTriggerProposal,
  );
  // A trigger with no reported fix has nothing to draw. It still reaches the
  // operator through the alarm panel; it just has no point to dispatch from.
  const triggerMarkers = useMemo(
    () => triggers.flatMap((t) => (t.latitude != null && t.longitude != null
      ? [{
        id: t.id,
        latitude: t.latitude,
        longitude: t.longitude,
        score: t.score,
        occurred_at: t.occurred_at,
        // The platform's own proposal, when automatic dispatch is armed. Absent on every
        // deployment that has not armed it, which is the default.
        proposed_drone_id: t.proposed_drone_id ?? null,
        proposed_altitude_m: t.proposed_altitude_m ?? null,
        proposal_mode: t.proposal_mode ?? null,
        proposal_expires_at: t.proposal_expires_at ?? null,
        proposal_considered: t.proposal_considered ?? null,
        // The account of an attempt that sent nothing. Omitted here until 2026-09-12, which
        // made the card's refusal panel dead code in the running app - see `TriggerMarker`.
        dispatch_refusal: t.dispatch_refusal ?? null,
      }]
      : [])),
    [triggers],
  );
  // Which trigger the open dispatch dialog is answering, so a successful
  // launch can be recorded against it. A ref, not state: nothing renders from
  // it, and it must not re-run the dialog's prop reconciliation.
  const answeringTriggerRef = useRef<string | null>(null);
  const handleDispatchFromTrigger = useCallback((trigger: TriggerMarker) => {
    answeringTriggerRef.current = trigger.id;
    // Pre-fills the dialog's required description, so the founder's flow -
    // choose altitude, choose drone, send - never stops on a validation toast
    // asking the operator to type something. The mission's own description
    // still names WHY it exists, which a blank field never did.
    const description = trigger.score != null
      ? `Person detected - ${Math.round(trigger.score)}% confidence`
      : 'Person detected';
    dispatchQuick({
      type: 'REQUEST_HERE',
      latitude: trigger.latitude,
      longitude: trigger.longitude,
      description,
    });
  }, []);
  const handleDispatchedFromDialog = useCallback((droneId: string, missionId: string) => {
    handleMissionLaunched(droneId, missionId);
    const triggerId = answeringTriggerRef.current;
    answeringTriggerRef.current = null;
    // The mission is already flying, so a failure to LINK it to the trigger is
    // not a failed dispatch and must not be reported as one. It is still told,
    // because the alternative is a red point that silently returns and invites
    // a second sortie at the same person.
    if (triggerId) {
      void markDispatched(triggerId, missionId).catch((e) => {
        toast.warning('Aircraft dispatched, but the trigger was not cleared', {
          description: e instanceof Error ? e.message : 'Dismiss it by hand once the sortie is under way.',
        });
      });
    }
  }, [handleMissionLaunched, markDispatched]);

  /**
   * Closing the dialog SPENDS the trigger it was answering.
   *
   * Without this the ref outlives a cancelled dispatch: the operator opens a
   * trigger, changes their mind, and the next unrelated "Dispatch drone here"
   * from anywhere on the map marks that old trigger `dispatched` against a
   * mission that never went near it - clearing the point on every operator's
   * COP and corrupting the one row that explains why an aircraft launched.
   */
  const handleDispatchDialogOpenChange = useCallback((open: boolean) => {
    if (!open) answeringTriggerRef.current = null;
    dispatchQuick({ type: 'SET_OPEN', open });
  }, []);
  const { coverageByMission, coverageDegraded, refreshCoverage } = useCopCoverage(
    subscribeCoverageEvent,
    subscribeRotationEvent,
  );
  const handoverLinks = useMemo(
    () => handoverLinksFrom(coverageByMission, coverageDegraded),
    [coverageByMission, coverageDegraded],
  );
  const {
    capableTypes: coverageCapableTypes, rotationStrategies: coverageRotationStrategies,
  } = useCoverageCapability();
  /**
   * The mission compose just created, whose operator asked for it to be held continuously.
   *
   * Held on the PAGE rather than in compose because compose stands down the moment the mission
   * exists - the wizard has to outlive the panel that offered it. Null closes the sheet.
   */
  const [coverageAttachTarget, setCoverageAttachTarget] = useState<{
    missionId: string;
    missionType: MissionType;
    hold: { latitude: number; longitude: number; altitude: number } | null;
    /** What compose assigned the mission, seeded as the rotation's first member. */
    assignedDroneId: string | null;
    /** The commit was a LAUNCH, so the sheet says so rather than "the mission is created". */
    launched: boolean;
  } | null>(null);
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);
  const [hoveredMissionId, setHoveredMissionId] = useState<string | null>(null);
  const selectedMissionIdRef = useRef<string | null>(null);

  /**
   * The SELECTED mission's rotation. Per-mission because `/rotation` and `/timeline` are
   * per-mission endpoints with no batch form - a fleet-wide prefetch would be N paired requests
   * every poll for a board where at most one mission is being looked at. The chip on every row
   * still comes from `useCopCoverage`'s one fleet-wide read.
   */
  /**
   * NOT simply `selectedMissionId`. Both rotation endpoints 404 with "No continuous coverage
   * attached to this mission", and most missions never have any - so passing every selection
   * through would 404 twice on pick and twice more every 15 s for as long as an ordinary mission
   * stayed selected, mark the hook degraded over a mission that is not broken, and log a warning
   * each time. The fleet-wide coverage read already knows which missions have a rotation; this
   * asks it first, and reuses the hook's existing no-selection path for the rest.
   */
  const rotationMissionId = selectedMissionId !== null && coverageByMission.has(selectedMissionId)
    ? selectedMissionId
    : null;
  const {
    rotation, timeline: rotationTimeline, degraded: rotationDegraded, lastReadAt: rotationLastReadAt,
    refreshRotation,
  } = useCopRotation(rotationMissionId, subscribeRotationEvent);

  selectedMissionIdRef.current = selectedMissionId;
  /**
   * Routes fetched for DRAFT missions the operator inspected. Drafts are not in
   * `activeMissions` (the server scopes that to scheduled/in_progress), so
   * their waypoints are fetched on first selection and cached for the session —
   * a preview, keyed by mission id, never mutated by the poll.
   */
  const [draftPreviews, setDraftPreviews] = useState<ReadonlyMap<string, ActiveMission>>(new Map());

  // Salience follows the panel (dim-on-pick only makes sense while the operator
  // can see WHY the picture dimmed): closing it stands the highlight down.
  useEffect(() => {
    if (!selection.missionsPanelOpen) {
      setSelectedMissionId(null);
      setHoveredMissionId(null);
    }
  }, [selection.missionsPanelOpen]);

  const { refresh: refreshMissionsQueue } = missionsQueue;
  /** Read by callbacks declared above these — they are the same functions, one render later. */
  const refreshMissionsQueueRef = useRef<(() => void) | null>(null);
  refreshMissionsQueueRef.current = refreshMissionsQueue;
  const loadMissionsRef = useRef<(() => void) | null>(null);
  loadMissionsRef.current = loadMissions;

  // Pans to the middle of the route's extent and keeps the operator's zoom -
  // a pick is a selection, not a request to rescale the picture (#723).
  const panToMission = useCallback((mission: ActiveMission) => {
    if (mission.waypoints.length === 0) return;
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    for (const w of mission.waypoints) {
      if (w.latitude < minLat) minLat = w.latitude;
      if (w.latitude > maxLat) maxLat = w.latitude;
      if (w.longitude < minLng) minLng = w.longitude;
      if (w.longitude > maxLng) maxLng = w.longitude;
    }
    window.dispatchEvent(
      new CustomEvent('panToMission', {
        detail: { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 },
      }),
    );
  }, []);

  const loadDraftPreview = useCallback(
    (missionId: string, panWhenLoaded: boolean) => {
      api
        .get<{ data: MissionDetailRow }>(`/api/missions/${missionId}`, {
          signal: AbortSignal.timeout(10_000),
        })
        .then((r) => {
          const preview: ActiveMission = {
            id: r.data.id,
            name: r.data.name,
            status: r.data.status,
            // Forwarded, not dropped (#700 review). This hand-built preview is a draft orbit's
            // only route to the map, so leaving the three out meant the ring simply never drew
            // for one - the same lone dot, reached by the one path the fix did not cover.
            type: r.data.type,
            orbit_radius_m: r.data.orbit_radius_m ?? null,
            orbit_direction: r.data.orbit_direction ?? null,
            assigned_drone_id: r.data.assigned_drone_id,
            drone_callsign: r.data.drone_callsign ?? null,
            waypoints: r.data.waypoints ?? [],
          };
          setDraftPreviews((prev) => new Map(prev).set(preview.id, preview));
          // Only fly the camera if this mission is still the one the operator
          // has picked — a slow response must not yank the map somewhere else.
          if (panWhenLoaded && selectedMissionIdRef.current === preview.id) panToMission(preview);
        })
        .catch(() => {
          // The pick cannot be honored, so it must not stand: a selection with
          // no route dims every other route with nothing promoted, which reads
          // as a display fault.
          if (selectedMissionIdRef.current === missionId) setSelectedMissionId(null);
          toast.error('Failed to load the mission route for preview');
        });
    },
    [panToMission],
  );

  const handleSelectMission = useCallback(
    (missionId: string | null) => {
      setSelectedMissionId(missionId);
      if (!missionId) return;
      // Picking a mission — from the panel or by clicking its route — is
      // explicit navigation to mission context, same as a dock pick opening
      // the dock sheet.
      dispatchSelection({ type: 'OPEN_MISSIONS_PANEL' });
      const active = activeMissions.find((m) => m.id === missionId);
      if (active) {
        panToMission(active);
        return;
      }
      const cached = draftPreviews.get(missionId);
      if (cached) {
        panToMission(cached);
        return;
      }
      loadDraftPreview(missionId, true);
    },
    [activeMissions, draftPreviews, panToMission, loadDraftPreview],
  );

  // #812: a toast whenever the platform voids an operator's approval, on ANY mission - the
  // operator flying gotos from the COP is exactly the one whose approval a gap can cost. Named
  // by mission, with OPEN to go to it, because the COP only reads the SELECTED mission's rotation.
  const orgTimezone = useOrgTimezone();
  const missionNameOf = useCallback(
    (missionId: string) => activeMissions.find((m) => m.id === missionId)?.name ?? null,
    [activeMissions],
  );
  useVoidedApprovalToasts(subscribeRotationEvent, {
    timezone: orgTimezone, missionName: missionNameOf, onOpen: handleSelectMission,
  });

  /**
   * The map's route overlay reads `activeMissions` (which carries waypoints),
   * not the queue — so a mission another operator launched, completed or
   * cancelled must also refresh THAT set, or the queue row and the drawn route
   * disagree until the 30 s poll. The frame itself has no waypoints, hence a
   * refetch rather than an in-place apply. The same frame invalidates any
   * cached draft route for that mission — the queue row updates live, and a
   * preview drawn from pre-edit waypoints would disagree with it for the rest
   * of the session; if that draft is the one on screen, refetch it in place.
   */
  useEffect(
    () =>
      subscribeMissionEvent((event) => {
        loadMissions();
        setDraftPreviews((prev) => {
          if (!prev.has(event.mission_id)) return prev;
          const next = new Map(prev);
          next.delete(event.mission_id);
          return next;
        });
        if (
          selectedMissionIdRef.current === event.mission_id &&
          event.kind !== 'deleted' &&
          event.mission?.status === 'draft'
        ) {
          loadDraftPreview(event.mission_id, false);
        }
      }),
    [subscribeMissionEvent, loadMissions, loadDraftPreview],
  );

  /**
   * A pick must not outlive the mission: when the picked id leaves the queue
   * (landed, cancelled, deleted — by anyone), the promotion stands down.
   * Without this the whole route picture stays dimmed with nothing promoted,
   * which at a glance reads as a display fault, and a deleted draft's cached
   * route would keep rendering as the promoted one.
   */
  useEffect(() => {
    if (!selectedMissionId || missionsQueue.loading) return;
    if (!missionsQueue.missions.some((m) => m.id === selectedMissionId)) {
      setSelectedMissionId(null);
    }
  }, [selectedMissionId, missionsQueue.loading, missionsQueue.missions]);

  /**
   * Routes the map draws: live/scheduled from the server, plus the previews for
   * the picked and hovered drafts.
   *
   * The HOVERED one is here too, and only including the selected one was a real
   * gap: `buildMissionFeatures` brightens a route by matching `hoveredMissionId`
   * against the missions it was handed, so a hovered draft that is not in this
   * list gets no feature and nothing on the map answers the hover. Every other
   * row in the queue lights its route up; a draft row silently did not.
   *
   * Only previews ALREADY cached are used. Hover is not a commitment — fetching
   * on it would fire a request per row the pointer crosses.
   */
  const mapMissions = useMemo<ActiveMission[]>(() => {
    const extras: ActiveMission[] = [];
    for (const id of [selectedMissionId, hoveredMissionId]) {
      if (!id) continue;
      if (activeMissions.some((m) => m.id === id)) continue;
      if (extras.some((m) => m.id === id)) continue;
      const preview = draftPreviews.get(id);
      if (preview) extras.push(preview);
    }
    return extras.length === 0 ? activeMissions : [...activeMissions, ...extras];
  }, [activeMissions, selectedMissionId, hoveredMissionId, draftPreviews]);

  const queueLiveCount = useMemo(
    () => missionsQueue.missions.filter((m) => m.status === 'in_progress').length,
    [missionsQueue.missions],
  );

  // ——— Reference lists for composing a mission on the COP ————————————————
  /**
   * The compose route, reachable from the callbacks below — they are declared
   * before the hook that owns it, and clearing a half-drawn route on open and on
   * close is part of what they do.
   */
  const composeRouteRef = useRef<DrawnRouteController | null>(null);
  const editRouteRef = useRef<DrawnRouteController | null>(null);
  /** Read by the entry points above, which are declared before the state they guard on. */
  const composeOpenRef = useRef(false);
  const editDirtyRef = useRef(false);
  /**
   * Which mission the route editor currently holds, readable from a response
   * that resolves later. `editRoute` is ONE controller reused across editing
   * sessions, so anything that writes into it from an async callback has to
   * prove the editor has not moved on first.
   */
  const editingIdRef = useRef<string | null>(null);
  /** The deep-link effect runs before these callbacks are declared. */
  const startComposeRef = useRef<(() => void) | null>(null);
  const startEditRouteRef = useRef<((missionId: string) => void) | null>(null);
  const [canvasRefData, setCanvasRefData] = useState<{
    drones: Drone[];
    pilots: Pilot[];
    sites: Site[];
    ready: boolean;
    loadError: boolean;
  }>({ drones: [], pilots: [], sites: [], ready: false, loadError: false });

  /** Issue order for canvas reference loads — only the newest open's response
   *  may write, so a hung request from a PREVIOUS open cannot blank the roster
   *  under an operator mid-plan when it finally fails. Same guard the board's
   *  own load uses. */
  const canvasRefSeq = useRef(0);

  const loadComposeRefData = useCallback(() => {
    // Fetched per open, not once: fleet and crew change on a human timescale,
    // but a panel opened hours into a shift must not see the shift-start roster.
    setCanvasRefData((prev) => ({ ...prev, ready: false, loadError: false }));
    const seq = ++canvasRefSeq.current;
    const signal = AbortSignal.timeout(10_000);
    Promise.all([
      api.get<{ data: Drone[] }>('/api/drones', { signal }),
      api.get<{ data: Pilot[] }>('/api/pilots', { signal }),
      api.get<{ data: Site[] }>('/api/sites', { signal }),
    ])
      .then(([d, p, s]) => {
        if (seq !== canvasRefSeq.current) return;
        setCanvasRefData({ drones: d.data, pilots: p.data, sites: s.data, ready: true, loadError: false });
      })
      // ready:true with loadError:true is the canvas's own contract for "the
      // lists failed": it renders its degraded notice instead of telling an
      // operator with a full fleet to go add a drone.
      .catch(() => {
        if (seq !== canvasRefSeq.current) return;
        setCanvasRefData({ drones: [], pilots: [], sites: [], ready: true, loadError: true });
      });
  }, []);

  const { droneRegistry, droneRegistryFailed, droneRegistryLoading } = useDroneRegistry();

  // One controller for whichever drone is selected. Keyed by id inside the hook,
  // which clears any in-flight ack state when the selection changes so a
  // previous aircraft's command never appears to belong to the new one.
  const droneCommand = useDroneCommand({
    droneId: selection.selectedDroneId ?? '',
    subscribeDroneAck,
    enabled: canDispatch && selection.selectedDroneId !== null,
  });

  // ——— The COP's one armed map mode ——————————————————————————————————————
  //
  // What a click on the map MEANS, as a single value. Fly-here, quick dispatch,
  // zone drawing and the dock route planner were four independent booleans held
  // in four different places, arbitrated only by the order of the tests in the
  // map's click handler; nothing stopped two being armed at once. They are all
  // variants of this now, and arming REPLACES, so the illegal state cannot be
  // written down. See lib/map-mode.ts.
  const [mapMode, dispatchMapMode] = useReducer(mapModeReducer, IDLE_MAP_MODE);
  const mapModeRef = useRef(mapMode);
  mapModeRef.current = mapMode;

  /**
   * Standing the OUTGOING mode's workflow down is part of arming, not a second
   * step each caller has to remember. Assigned during render because it closes
   * over state declared below; read only from callbacks.
   */
  const standDownModeRef = useRef<(prev: MapMode, next: MapMode) => void>(() => {});

  const armMapMode = useCallback((next: MapMode) => {
    standDownModeRef.current(mapModeRef.current, next);
    dispatchMapMode({ type: 'ARM', mode: next });
  }, []);

  const disarmMapMode = useCallback(() => {
    standDownModeRef.current(mapModeRef.current, IDLE_MAP_MODE);
    dispatchMapMode({ type: 'DISARM' });
  }, []);

  /**
   * Every range-and-bearing line on the map, the last one possibly still open.
   *
   * Here rather than inside the `MapMode` value, the same way the zone being
   * drawn is: the mode says the map is armed for measuring, and the tool owns
   * what has been measured.
   *
   * INDEPENDENT of the armed mode (D10). A measurement is an answer the operator
   * asked for, and the moment it is most useful is the moment they act on it -
   * dispatch there, fly there, read it out. Tying it to the mode meant every one
   * of those acts deleted it first: taking manual authority, opening a mission
   * route for revision and arming any other tool all stand the mode down, and
   * all three would have wiped the reading with no toast and no trace. A line is
   * removed by its right-click Delete, all of them by CLEAR in the map key's
   * readout row, and by nothing else.
   */
  const [rangeBearingMeasurements, setRangeBearingMeasurements] =
    useState<RangeBearingMeasurements>(NO_MEASUREMENTS);
  const rangeBearingArmed = mapMode.kind === 'range-bearing';

  const handleToggleRangeBearing = useCallback(() => {
    if (mapModeRef.current.kind === 'range-bearing') disarmMapMode();
    else armMapMode({ kind: 'range-bearing' });
  }, [armMapMode, disarmMapMode]);

  const clearRangeBearing = useCallback(() => setRangeBearingMeasurements(NO_MEASUREMENTS), []);
  const finishRangeBearingLine = useCallback(() => setRangeBearingMeasurements(finishMeasureLine), []);
  const deleteRangeBearingLine = useCallback(
    (index: number) => setRangeBearingMeasurements((current) => removeMeasureLine(current, index)),
    [],
  );

  // The double-click: the line is done AND the ruler is put down, so the map
  // is back to the live picture and the crosshair goes. Another measurement is
  // one press of the ruler key away, and the finished ones stay on the map.
  const handleFinishRangeBearingLine = useCallback(() => {
    finishRangeBearingLine();
    if (mapModeRef.current.kind === 'range-bearing') disarmMapMode();
  }, [finishRangeBearingLine, disarmMapMode]);

  // Putting the ruler down - by its key, Esc, another tool taking the map, or
  // the map going compact - ends the open line, so arming it again starts a new
  // line instead of extending one the operator had walked away from. An effect
  // on the armed flag rather than a line in the stand-down, because the compact
  // path disarms without going through the stand-down.
  useEffect(() => {
    if (!rangeBearingArmed) finishRangeBearingLine();
  }, [rangeBearingArmed, finishRangeBearingLine]);

  /**
   * One object for the map, memoised so the map's setData effect is not woken
   * by this page re-rendering for an unrelated reason.
   */
  const rangeBearing = useMemo(() => ({
    armed: rangeBearingArmed,
    onToggle: handleToggleRangeBearing,
    measurements: rangeBearingMeasurements,
    onMapClick: (lat: number, lng: number) =>
      setRangeBearingMeasurements((current) => addMeasurePoint(current, { lat, lng })),
    onFinishLine: handleFinishRangeBearingLine,
    onDeleteLine: deleteRangeBearingLine,
    onClear: clearRangeBearing,
  }), [
    rangeBearingArmed,
    handleToggleRangeBearing,
    rangeBearingMeasurements,
    handleFinishRangeBearingLine,
    deleteRangeBearingLine,
    clearRangeBearing,
  ]);

  /**
   * Zone authoring, held here because the panel and the drawn shape are in
   * different subtrees since slice 8.
   *
   * The panel is the right column's fourth tab now (`CopSidePanel`), and the
   * shape it is collecting is painted by `DroneMap` inside `CopMapStage`. This
   * page renders both, so it owns the one reducer they read; a copy on either
   * side is how a half-drawn polygon ends up on screen and not in the thing that
   * saves it. It used to live in `CopMapStage`, which hosted both.
   */
  const [zone, dispatchZone] = useReducer(zoneReducer, initialZoneState);
  /** Read from callbacks that must not re-create on every zone keystroke. */
  const zoneShowPanelRef = useRef(false);
  zoneShowPanelRef.current = zone.showPanel;
  /** Which shape the operator is drawing, if the map is armed for zones at all. */
  const zoneShape = zoneDrawShape(mapMode);
  // Held outside ZonesPanel so the map can be painted in the colour the operator
  // has selected. Deriving it on every render keeps one source of truth: the
  // picker and the preview cannot disagree, and there is no second copy to fall
  // out of step when the zone TYPE moves the default.
  const [zoneForm, dispatchZoneForm] = useReducer(zoneFormReducer, INITIAL_ZONE_FORM);
  const zoneDrawColor = resolveZoneColor(zoneForm.type, zoneForm.color);

  const handleSaveGeofence = useCallback(async (data: SaveGeofenceData) => {
    try {
      await api.post('/api/geofences', data);
      toast.success('Zone created');
      loadGeofences();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save zone'));
    }
  }, [loadGeofences]);

  const handleDeleteGeofence = useCallback(async (id: string) => {
    try {
      await api.delete(`/api/geofences/${id}`);
      toast.success('Zone deleted');
      loadGeofences();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete zone'));
    }
  }, [loadGeofences]);

  const handleZoomToGeofence = useCallback((geometry: GeoJSON.Geometry) => {
    if (geometry.type === 'Polygon' && geometry.coordinates[0]?.length > 0) {
      const coords = geometry.coordinates[0] as number[][];
      let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
      for (const [lng, lat] of coords) {
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
      }
      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;
      window.dispatchEvent(new CustomEvent('flyToSite', { detail: { lat: centerLat, lng: centerLng } }));
    }
  }, []);

  /**
   * Leave the zones sheet: throw the half-drawn shape away and stand the map
   * down, but ONLY the zone arm.
   *
   * Kind-scoped, like every other release in this file. A blanket `DISARM`
   * would cancel an arm zones never owned - the dock planner holds the map
   * whenever a dock is selected, so pressing ZONES to check a boundary and then
   * pressing DOCK again would silently drop the plan, and the next map click
   * would place no waypoint.
   */
  const leaveZones = useCallback(() => {
    dispatchZone({ type: 'CLOSE_PANEL' });
    if (mapModeRef.current.kind === 'zone-draw') {
      dispatchMapMode({ type: 'DISARM_KIND', kind: 'zone-draw' });
    }
  }, []);

  /**
   * Open a sheet from somewhere other than the tab strip, leaving zones first.
   *
   * The top bar's Missions key, the command bar's DETAILS key and a dock press
   * all dispatch selection state directly, and the zones sheet outranks all
   * three. Without this they latch their own pressed state and change nothing:
   * a key that lights up and does nothing is worse than one that is not there.
   */
  const openSheetFromOutside = useCallback((open: () => void) => {
    if (zoneShowPanelRef.current) leaveZones();
    open();
  }, [leaveZones]);

  /** What the map paints while a zone is being drawn. */
  const zoneDraw = useMemo(() => ({
    points: zone.drawPoints,
    polygonClosed: zone.polygonClosed,
    circleCenter: zone.circleCenter,
    circleRadius: zone.circleRadius,
    color: zoneDrawColor,
    onMapClick: (lat: number, lng: number) =>
      dispatchZone({ type: 'MAP_CLICK', lat, lng, shape: zoneShape }),
  }), [zone.drawPoints, zone.polygonClosed, zone.circleCenter, zone.circleRadius, zoneDrawColor, zoneShape]);

  /**
   * Start composing on the live map.
   *
   * Arming REPLACES, so this stands down whatever else held the map — and the
   * air picture keeps painting underneath, which is the whole reason compose
   * moved off the full-screen canvas.
   */
  const startCompose = useCallback(() => {
    // Refused rather than clobbered: an unsaved route revision is real work, and
    // silently replacing it with a blank compose is the one outcome no operator
    // would have chosen.
    if (editDirtyRef.current) {
      toast.error('Finish or discard the route you are editing first');
      return;
    }
    setEditing(null);
    composeRouteRef.current?.clear();
    setComposeOpen(true);
    // 022. A new mission starts as a route — the column's own default, and what compose did
    // before types existed. Reset explicitly so the previous mission's type is not inherited.
    setComposeType(DEFAULT_MISSION_TYPE);
    loadComposeRefData();
    armMapMode({ kind: 'mission-compose', shape: geometryShapeForType(DEFAULT_MISSION_TYPE) });
    dispatchSelection({ type: 'OPEN_MISSIONS_PANEL' });
  }, [loadComposeRefData, armMapMode]);

  startComposeRef.current = startCompose;

  const closeCompose = useCallback(() => {
    setComposeOpen(false);
    composeRouteRef.current?.clear();
    dispatchMapMode({ type: 'DISARM_KIND', kind: 'mission-compose' });
  }, []);

  /**
   * Open a saved mission's route for revision.
   *
   * The status check is here as well as on the row that offers it and on the
   * server: an executing mission is read-only on every surface, and "the button
   * is hidden" is not the same claim as "this cannot happen".
   */
  const startEditRoute = useCallback((missionId: string) => {
    if (composeOpenRef.current && (composeRouteRef.current?.waypoints.length ?? 0) > 0) {
      toast.error('Finish or cancel the mission you are composing first');
      return;
    }
    api
      .get<{ data: MissionDetailRow & { default_altitude?: number | null } }>(
        `/api/missions/${missionId}`,
        { signal: AbortSignal.timeout(10_000) },
      )
      .then((res) => {
        const row = res.data;
        if (!isRouteEditable(row.status)) {
          toast.error(
            row.status === 'in_progress'
              ? 'This mission is in the air — its route cannot be changed'
              : `A ${row.status} mission's route cannot be changed`,
          );
          return;
        }
        // Each waypoint's OWN stored altitude comes back with it. Re-deriving it from the
        // mission default would be the same defect the save path had: a route whose points
        // differ in altitude would be flattened the moment it was opened for revision.
        const drawn = toDraftWaypoints(row.waypoints ?? []);
        const missionAltitudeM = row.default_altitude ?? CANVAS_ALT_DEFAULT_M;
        editRouteRef.current?.load(drawn);
        setEditing({
          id: row.id,
          name: row.name,
          status: row.status,
          type: row.type,
          altitudeM: missionAltitudeM,
          storedSignature: drawnRouteSignature(drawn, missionAltitudeM),
        });
        setComposeOpen(false);
        dispatchSelection({ type: 'OPEN_MISSIONS_PANEL' });
        // Opens LOCKED. Revising a commitment is an explicit act, so the panel's
        // own Revise control is what arms the map.
        //
        // Every mode that PLACES INTO THE PLAN OVERLAY stands down, not just
        // compose. This panel takes that overlay (`planOverlay` branches on
        // `editing` before anything else), so any such mode left armed goes on
        // claiming clicks that now land in THIS route: with the dock planner
        // still holding the map — which it does whenever a dock is selected,
        // since opening the missions tab does not deselect it — every click
        // appended a waypoint to a route the panel was showing as locked, under
        // a banner still describing the dock plan. A kind-scoped release could
        // not express that, because the offending mode is a different kind.
        //
        // Scoped rather than blanket, and the distinction is not cosmetic:
        // standing `zone-draw` down DESTROYS WORK. Its geometry lives in
        // this file's own zoneAuthoring reducer, and ZonesPanel unmounts the moment the shape goes
        // null, and re-arming dispatches RESET_DRAWING — so a half-drawn
        // restricted zone would vanish, named and coloured, with no toast and
        // no undo, because the operator nudged a waypoint.
        //
        // Stated as an EXEMPTION rather than a list, because that is what it is:
        // everything stands down except the one mode whose stand-down cannot be
        // undone. Enumerating the modes to disarm meant `goto` survived — and
        // `goto` is the worst one to leave armed, because its click does not
        // place a marker, it COMMANDS AN AIRCRAFT TO MOVE (map-mode.ts says so).
        // With the drone selection deliberately preserved across
        // OPEN_MISSIONS_PANEL, an operator who armed Fly here and then opened a
        // route for revision would click a waypoint to inspect it — the gesture
        // that means "select" in every plan mode — and fly the aircraft there.
        // A new variant now has to argue its way onto the exempt list instead of
        // being silently omitted from an inclusion list.
        if (mapModeRef.current.kind !== 'zone-draw') disarmMapMode();
      })
      .catch(() => toast.error('Failed to load the mission route'));
  }, [disarmMapMode]);

  /**
   * Deep links from the library.
   *
   * `/?compose=1` starts a new mission; `/?mission=<id>&edit=1` opens that
   * mission's route for revision. Both come from `/missions`, which is the
   * catalog — the COP is where a route is actually drawn, so the link is how
   * one hands over to the other with the work already set up.
   *
   * Consumed once per arrival: `handledDeepLinkRef` keeps a re-render (or the
   * operator closing the panel) from re-arming it under them.
   */
  const handledDeepLinkRef = useRef<string | null>(null);
  const composeParam = searchParams.get('compose');
  const missionParam = searchParams.get('mission');
  const editParam = searchParams.get('edit');
  useEffect(() => {
    const key = `${composeParam ?? ''}|${missionParam ?? ''}|${editParam ?? ''}`;
    if (key === '||' || handledDeepLinkRef.current === key) return;
    handledDeepLinkRef.current = key;
    if (composeParam === '1') {
      startComposeRef.current?.();
      return;
    }
    if (missionParam && editParam === '1') startEditRouteRef.current?.(missionParam);
  }, [composeParam, missionParam, editParam]);

  startEditRouteRef.current = startEditRoute;

  /**
   * End a running sortie (#574).
   *
   * THIS REPLACED A PURE STATUS WRITE, and the difference is the whole point.
   * `PUT /api/missions/:id { status: 'completed' }` was honest about the mission
   * row and silent about the airframe: pressed while the aircraft was still
   * flying, it marked the mission completed and left the aircraft airborne with
   * no mission bound to it. The server now resolves the airframe state and
   * branches - the aircraft is commanded home, or nothing is commanded and the
   * mission closes - so the operator no longer has to know which case they are in.
   *
   * The two outcomes read differently on purpose. `completed` is over. `returning`
   * is NOT: the mission is still in progress and stays that way until the landing
   * closes it, so the toast says what is going to happen rather than claiming it
   * already has. It is irreversible either way, so the panel arms and confirms
   * before this is reached.
   */
  const [stoppingMissionId, setStoppingMissionId] = useState<string | null>(null);
  /**
   * Missions this operator has stopped whose aircraft is on its way home.
   *
   * CLIENT-LOCAL, and deliberately so. The mission row carries no "stopping"
   * field: the mission genuinely is still `in_progress`, and every other operator
   * sees exactly that plus an aircraft visibly flying home on the map. This exists
   * so the person who pressed STOP can see that the press did something during the
   * minutes between the command and the touchdown. It does not survive a reload,
   * which is the honest cost of not inventing a state the server does not hold.
   */
  const [returningMissionIds, setReturningMissionIds] = useState<ReadonlySet<string>>(() => new Set());
  const handleStopMission = useCallback((mission: StoppableMission) => {
    // NAMED IN EVERY MESSAGE. Two of these stopped in a row otherwise stack two identical
    // toasts, and if one of them was the refusal nothing says which airframe is coming home.
    const who = mission.drone_callsign ? `${mission.drone_callsign} · ${mission.name}` : mission.name;
    const announce = (m: MissionStopMessage) => {
      if (m.marksReturning) setReturningMissionIds((prev) => new Set(prev).add(mission.id));
      const options = { description: m.description, duration: m.duration };
      if (m.tone === 'success') toast.success(m.title, options);
      else if (m.tone === 'warning') toast.warning(m.title, options);
      else toast.error(m.title, options);
    };

    setStoppingMissionId(mission.id);
    api
      .post<{ data?: { outcome?: string; completes_on_landing?: boolean } }>(
        `/api/missions/${mission.id}/stop`,
        {},
      )
      .then((res) => {
        announce(missionStopMessage({
          who,
          outcome: res?.data?.outcome,
          completesOnLanding: res?.data?.completes_on_landing,
        }));
        refreshMissionsQueueRef.current?.();
        loadMissionsRef.current?.();
      })
      .catch((err: unknown) => announce(missionStopFailure({
        who,
        message: getApiErrorMessage(err, 'Failed to stop the mission'),
        code: err instanceof ApiRequestError ? err.code : undefined,
      })))
      .finally(() => setStoppingMissionId(null));
  }, []);

  /**
   * The RETURNING marker outlives nothing.
   *
   * Dropped the moment the mission is no longer in the queue as `in_progress` -
   * the landing completed it, somebody cancelled it, or it left the COP's queue.
   * Without this the set only grows, and the marker would still be sitting on a
   * mission that had already closed. Held while the queue is loading, because an
   * empty list before the first response is not evidence that anything ended.
   */
  useEffect(() => {
    if (missionsQueue.loading) return;
    setReturningMissionIds((prev) => {
      if (prev.size === 0) return prev;
      const stillRunning = new Set(
        missionsQueue.missions.filter((m) => m.status === 'in_progress').map((m) => m.id),
      );
      const next = new Set([...prev].filter((id) => stillRunning.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [missionsQueue.missions, missionsQueue.loading]);

  const closeEditRoute = useCallback(() => {
    setEditing(null);
    editRouteRef.current?.clear();
    dispatchMapMode({ type: 'DISARM_KIND', kind: 'mission-edit' });
  }, []);

  const closeComposeAndRefresh = useCallback(() => {
    closeCompose();
    // The mission_event frame usually lands first; these are the no-wait path,
    // and the shared reconcile settles the two (missionQueueShared).
    refreshMissionsQueue();
    loadMissions();
  }, [closeCompose, refreshMissionsQueue, loadMissions]);

  // "Fly here" pick mode: armed from the command bar, resolved by the next map
  // click. Reset whenever the selected drone changes so an armed pick never
  // lands on a different aircraft than the operator armed it for.
  const gotoArmed = mapMode.kind === 'goto';
  useEffect(() => {
    dispatchMapMode({ type: 'DISARM_KIND', kind: 'goto' });
  }, [selection.selectedDroneId]);

  // The lens the operator last PRESSED, held here rather than in the overlay
  // because two overlays for one aircraft can be mounted at once (the details
  // panel and the manual stage) and both must show the same pending request.
  //
  // Reset on selection change for the same reason the armed GO-TO above is: a
  // request left over from the previous aircraft would render as this one's
  // pending lens, on a camera nobody has commanded. It is deliberately NOT
  // cleared when the readback agrees — the key stops reading as pending the
  // moment `cameraLens` matches, so clearing it as well would just be a second
  // way of saying the same thing, on a different clock.
  const [requestedLens, setRequestedLens] = useState<CameraLens | null>(null);
  useEffect(() => {
    setRequestedLens(null);
  }, [selection.selectedDroneId]);

  // The altitude the next GO-TO will fly to. Re-seeded every time pick mode is
  // armed (see handleToggleGoto), which is also what carries an operator's
  // choice across repeated repositions: after the first GO-TO the aircraft IS at
  // the altitude they picked, so seeding from current altitude preserves it.
  const [gotoAltitudeM, setGotoAltitudeM] = useState(() => altitude.profile.default_m);
  const [gotoUnit, setGotoUnit] = useState<AltitudeUnit>(altitude.profile.unit);

  const droneList = useMemo(() => Array.from(drones.values()), [drones]);

  // Everything airborne right now, with its last-report time — feeds the dock
  // sheet's bay rows (IN AIR state, freshness-qualified) and the landing
  // detector below. `status === 'flying'` is a per-frame derivation in useWebSocket - the
  // aircraft's own `is_flying` where it reports one, altitude>2 otherwise - with no hysteresis,
  // and this map is session-scoped (empty on
  // a fresh COP load even mid-sortie) — display state only. The check that
  // actually holds is server-side: the GO/NO-GO gate refuses dispatch for a
  // drone with an in-progress flight (dock-go-nogo.ts), and the flight row
  // outlives both wobbling frames and a lost link.
  // The altitude rides along with the stamp because a LOST link is a guess, and the guess is only
  // good while the last position we saw was off the pad — see `bayAirborneFrom`. Without it, a DJI
  // held at ~1 m whose publisher then dies stays IN AIR for the session and keeps its own LAUNCH
  // button disabled.
  const airborneDrones = useMemo(
    () =>
      new Map(
        droneList
          .filter((d) => d.status === 'flying')
          .map((d) => [d.drone_id, { lastSeenAt: d.last_seen_at, altitudeM: d.altitude }]),
      ),
    [droneList],
  );

  // #811: every aircraft whose latest frame carries a precision-landing state, for the dock card.
  // Freshness is judged where it is rendered (`tagLandingView`), against the frame's own stamp.
  const tagLandings = useMemo<TagLandingsByDrone>(
    () =>
      new Map(
        droneList.flatMap((d) =>
          d.tag_landing
            ? [[d.drone_id, { tag: d.tag_landing, lastSeenAt: d.last_seen_at, callsign: d.callsign }] as const]
            : [],
        ),
      ),
    [droneList],
  );

  /**
   * Refresh the mission set the moment an aircraft stops flying.
   *
   * A landing is what ends a mission: the backend closes the flight and marks the
   * mission `completed` (services/flights.ts), and no WebSocket channel announces
   * it. Waiting for the poll would leave the panel reporting IN_PROGRESS, and the
   * map drawing an active route, for an aircraft already back on the tray. This
   * watches the same signal the backend does, so the two agree within a tick.
   *
   * The poll stays as the backstop: the backend closes the flight from telemetry
   * it may process just after this fires, so an early refresh can still catch the
   * mission mid-flight.
   */
  const flyingDronesRef = useRef<ReadonlySet<string>>(new Set());
  useEffect(() => {
    const anyLanded = [...flyingDronesRef.current].some((id) => !airborneDrones.has(id));
    flyingDronesRef.current = new Set(airborneDrones.keys());
    if (anyLanded) loadMissions();
  }, [airborneDrones, loadMissions]);

  // 320 px, and remembered per operator rather than recomputed from the
  // viewport on every load. It used to open at 30% of the screen clamped to
  // 560, which on any display worth flying from meant 560 - and an operator who
  // dragged it in got 560 back next session. See `useSidePanelWidth`.
  const { width: dockSheetWidth, setWidth: setDockSheetWidth } = useSidePanelWidth({
    operator: user?.email ?? null,
  });
  const [dockRegistry, setDockRegistry] = useState<Dock[]>([]);
  // Refreshed every minute rather than loaded once. The registry carries each dock's position check,
  // which the survey re-judges every 10 minutes and a pin move re-judges at once; loaded once, the
  // dock panel kept showing the page-load answer for a whole shift while the alert bell said
  // otherwise (docs/PLANS/dock-geoposition, slice 2 review). A failed refresh keeps the last list.
  useEffect(() => {
    const REFRESH_MS = 60_000;
    let cancelled = false;
    const load = () => {
      api
        .get<{ data: Dock[] }>('/api/docks')
        .then((r) => {
          if (!cancelled) setDockRegistry(r.data);
        })
        .catch(() => {
          console.warn('Failed to load dock registry for COP');
        });
    };
    load();
    const handle = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  // Position falls back to the linked site when the dock has no lat/lng of its
  // own, so a coordless-but-sited dock still renders on the COP (issue #39).
  const dockMarkers = useMemo<DockMarker[]>(
    () => dockRegistry.flatMap((d) => {
      const pos = resolveDockPosition(d, sites);
      if (!pos) return [];
      const live = dockMap.get(d.id);
      return [{
        id: d.id,
        callsign: live?.callsign ?? d.callsign,
        latitude: pos.latitude,
        longitude: pos.longitude,
        status: live?.status ?? d.status,
        is_online: live?.is_online ?? d.is_online,
        safe_land: d.safe_land_lat !== null && d.safe_land_lon !== null
          ? { latitude: d.safe_land_lat, longitude: d.safe_land_lon }
          : null,
      }];
    }),
    [dockRegistry, dockMap, sites],
  );

  const dockRoster = useMemo<DockMarker[]>(
    () => dockRegistry.map((d) => {
      const live = dockMap.get(d.id);
      // The roster lists every dock (coordless ones too); resolving here keeps a
      // sited dock's "fly to" correct instead of sending the map to 0,0.
      const pos = resolveDockPosition(d, sites);
      return {
        id: d.id,
        callsign: live?.callsign ?? d.callsign ?? d.name,
        latitude: pos?.latitude ?? 0,
        longitude: pos?.longitude ?? 0,
        status: live?.status ?? d.status,
        is_online: live?.is_online ?? d.is_online,
      };
    }),
    [dockRegistry, dockMap, sites],
  );

  const copDrones = useMemo(() => {
    if (!siteId) return droneList;
    if (allowedDroneIds === null) return [];
    return droneList.filter((d) => allowedDroneIds.has(d.drone_id));
  }, [droneList, siteId, allowedDroneIds]);

  /** Live tracks PLUS everything parked in a bay - see `dispatchableDrones`. */
  const dispatchDroneOptions = useMemo(
    () => dispatchableDrones(copDrones, droneRegistry, dockRegistry, siteId),
    [copDrones, droneRegistry, dockRegistry, siteId],
  );

  // Same aircraft the map draws, keyed for lookup. The missions panel asks this
  // whether it knows where a mission's aircraft is before offering to fly the
  // camera there; resolving from copDrones (not the unfiltered list) means a
  // site filter can never send the camera to an airframe this picture excludes.
  const copDroneTracks = useMemo(
    () => new Map(copDrones.map((d) => [d.drone_id, d])),
    [copDrones],
  );

  const copGeofences = useMemo(() => {
    if (!siteId) return geofences;
    return geofences.filter((g) => !g.site_id || g.site_id === siteId);
  }, [geofences, siteId]);

  const copSites = useMemo(() => {
    if (!siteId) return sites;
    return sites.filter((s) => s.id === siteId);
  }, [sites, siteId]);

  const selectedDrone = selection.selectedDroneId
    ? copDrones.find((d) => d.drone_id === selection.selectedDroneId)
    : undefined;
  const activeSelectedId = selectedDrone ? selection.selectedDroneId : null;

  /* ---------------------------------------------------------------------------
     Floating camera windows.

     Owned here because this component is the only one that survives a selection
     change. Every other candidate — the dock sheet, the details panel — is
     unmounted the moment the operator clicks something else, which is exactly
     how the dock's Internal/External windows used to vanish without anyone
     pressing their X.
     --------------------------------------------------------------------------- */

  /** Where each window was last dragged, keyed by `cameraWindowKey`. Geometry is
   *  deliberately not in the selection reducer: it is a UI position, not part of
   *  what the operator has selected, and a drag would otherwise write selection
   *  state on every pointer move. */
  const [cameraWindowPositions, setCameraWindowPositions] = useState<
    Record<string, FloatingWindowPosition>
  >({});
  const moveCameraWindow = useCallback((key: string, position: FloatingWindowPosition) => {
    setCameraWindowPositions((prev) => ({ ...prev, [key]: position }));
  }, []);

  /** And what they were resized to. Held here for the reason #645 held it in
   *  `DockSidebar`: an operator who widened the picture to watch a tray approach
   *  must not get the opening size back next time. That ownership moved up here
   *  with the rest of the window's, since the sidebar no longer outlives it. */
  const [cameraWindowSizes, setCameraWindowSizes] = useState<
    Record<string, FloatingWindowSize>
  >({});
  const resizeCameraWindow = useCallback((key: string, size: FloatingWindowSize) => {
    setCameraWindowSizes((prev) => ({ ...prev, [key]: size }));
  }, []);

  /** The cascade step each open window holds, so a window opening now steps
   *  clear of every one already on screen — see `freeCascadeSlot` for why the
   *  position in the list will not do. Entries for closed windows are dropped at
   *  the next open rather than on close, because not every close comes through
   *  `closeCameraWindow`; a stale entry is inert until then, since only open
   *  windows are ever read. */
  const [cameraWindowSlots, setCameraWindowSlots] = useState<Record<string, number>>({});

  /**
   * What each camera's PLAYER is doing, keyed by stream id.
   *
   * Owned here because the players are here. The FEED segment used to hold this
   * map and fill it from its own inline picture; it draws no picture now, so a
   * verdict it could not observe would be no verdict at all — and the health dot
   * on the FEED button exists precisely to say "your video is black" when the
   * registry row still reads LIVE.
   *
   * One entry per camera, so two cameras of one airframe cannot overwrite each
   * other. `painted` is kept because `loading` means two different things:
   * before a first frame it is an ordinary connect, after one it is a RECONNECT
   * over a frozen picture, and only the second deserves the operator's
   * attention.
   *
   * A window that closes withdraws its verdict rather than leaving a stale one
   * behind: with no player, "we cannot see the picture" is the honest answer,
   * and the dot falls back to what the registry can prove.
   */
  const [videoPlayerStates, setVideoPlayerStates] = useState<
    Record<string, { state: HlsStreamState; painted: boolean }>
  >({});
  const reportVideoPlayerState = useCallback((id: string, state: HlsStreamState | null) => {
    setVideoPlayerStates((prev) => {
      if (state === null) {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      const painted = prev[id]?.painted === true || state === 'playing';
      if (prev[id]?.state === state && prev[id]?.painted === painted) return prev;
      return { ...prev, [id]: { state, painted } };
    });
  }, []);

  /** The cameras of the SELECTED aircraft that are in a window — the placement
   *  answer every existing consumer (FEED, the manual stage, `stageCamera`)
   *  asks for, unchanged in shape by the storage growing a drone id. */
  const selectedPoppedCameras = useMemo(
    () => poppedCamerasFor(selection.cameraWindows, selection.selectedDroneId),
    [selection.cameraWindows, selection.selectedDroneId],
  );

  /** This dock's camera slots that already have a window, so the sidebar's keys
   *  can show which are taken. */
  const openDockCameras = useMemo(
    () =>
      selection.cameraWindows.flatMap((w) =>
        w.kind === 'dock' && w.dockId === selection.selectedDockId ? [w.cam] : [],
      ),
    [selection.cameraWindows, selection.selectedDockId],
  );

  /**
   * A dock window's own stream path and name, resolved from its stored dock id.
   *
   * By id, never from `selectedRegistryDock`: a window opened on dock A stays on
   * dock A when the operator selects dock B. Reading the selection here is how
   * the windows used to survive a dock switch but silently repoint at the new
   * dock's cameras while still titled the old one's.
   *
   * Null while the registry has not loaded, or after the row is gone — the
   * window says so rather than playing a URL with an empty path.
   */
  const resolveDockCameraSource = useCallback(
    (dockId: string) => {
      const row = dockRegistry.find((d) => d.id === dockId);
      if (!row?.topic_prefix) return null;
      return {
        topicPrefix: row.topic_prefix,
        callsign: dockMap.get(dockId)?.callsign ?? row.callsign ?? dockId.slice(-6).toUpperCase(),
      };
    },
    [dockRegistry, dockMap],
  );

  /** The name a window is showing right now, for anything that has to talk
   *  ABOUT the window rather than draw it. Same resolution order the window
   *  itself uses. */
  const resolveCameraWindowCallsign = useCallback(
    (target: CameraWindow) =>
      target.kind === 'drone'
        ? copDroneTracks.get(target.droneId)?.callsign?.trim() || null
        : resolveDockCameraSource(target.dockId)?.callsign ?? null,
    [copDroneTracks, resolveDockCameraSource],
  );

  /**
   * The camera pose an aircraft is REPORTING, for any window, selected or not.
   *
   * Split from `cameraControl` deliberately. Withholding the command inputs from
   * a background window is right (it would slew the selected aircraft's gimbal);
   * withholding the READOUT is not. The overlay renders the pose in every state
   * on purpose - "a viewer of a dock aircraft is entitled to read it while every
   * control is withheld" - and each field falls back to NOT REPORTED when it is
   * absent. So withholding the whole object made a background window print the
   * product's dead-sensor token for a gimbal that is streaming fine.
   */
  const resolveDronePose = useCallback(
    (droneId: string) => {
      const drone = copDroneTracks.get(droneId);
      if (!drone) return undefined;
      return {
        gimbalPitchDeg: drone.gimbal_pitch,
        gimbalYawDeg: drone.gimbal_yaw,
        zoomRatio: drone.zoom_ratio,
        cameraLens: drone.camera_lens ?? null,
      };
    },
    [copDroneTracks],
  );

  const raiseCameraWindow = useCallback((target: CameraWindow) => {
    dispatchSelection({ type: 'RAISE_CAMERA_WINDOW', window: target });
  }, []);

  /**
   * Give a window about to open the lowest cascade step nothing on screen holds.
   *
   * Allocate against the slots held by windows that are actually OPEN, and keep
   * only those. A window can leave the set without passing through
   * `closeCameraWindow` — FEED's dock-back key, the manual stage's "bring it all
   * back", and now the map click retiring the previous aircraft's transient
   * window all drop one straight out of the reducer — so a record that were only
   * ever added to would leak a slot per such close, push every later window
   * further down-right, and end with them all clamped into the bottom-right
   * corner overlapping each other, which is the collision this allocator exists
   * to prevent.
   *
   * `openAfter` is that set of survivors. It defaults to the windows open right
   * now, which is right for every deliberate press — but the map click retires
   * the previous aircraft's transient window in the SAME dispatch, so counting
   * it as open there would step the newcomer clear of a place nothing occupies
   * and leave the follow window alternating between two positions on the map.
   */
  const allocateCascadeSlot = useCallback(
    (target: CameraWindow, openAfter: CameraWindow[] = selection.cameraWindows) => {
      const openKeys = new Set(openAfter.map(cameraWindowKey));
      setCameraWindowSlots((held) => {
        const stillOpen = Object.fromEntries(
          Object.entries(held).filter(([key]) => openKeys.has(key)),
        );
        return { ...stillOpen, [cameraWindowKey(target)]: freeCascadeSlot(stillOpen) };
      });
    },
    [selection.cameraWindows],
  );

  /**
   * Refuse an open past the cap, out loud.
   *
   * The reducer refuses too, so the invariant does not rest on this check — but
   * a reducer cannot say why, and a camera key that silently does nothing is
   * indistinguishable from a broken one.
   */
  const openCameraWindow = useCallback(
    (target: CameraWindow) => {
      // Re-opening a window that is already open is a no-op, not an overflow —
      // the cap must not refuse a press that would have changed nothing.
      const alreadyOpen = selection.cameraWindows.some((w) => sameCameraWindow(w, target));
      // An already-open camera is RAISED, not re-opened. Pressing the sidebar
      // key for a camera you already have open means "show me it" - and since
      // a window can now be buried under a resized one, a press that does
      // nothing is a control that names the missing picture and refuses to
      // produce it.
      if (alreadyOpen) {
        raiseCameraWindow(target);
        return;
      }
      if (cameraWindowsFull(selection.cameraWindows)) {
        // Named, not counted. Windows can be dragged anywhere on the map, so
        // "four are open" leaves the operator hunting for them; the list says
        // which four, and therefore which one they no longer need.
        toast.error(`${MAX_CAMERA_WINDOWS} camera windows already open`, {
          // Named the way the windows name THEMSELVES - live name first, the
          // snapshot second. The toast exists so the operator can find the one
          // to close; listing it under an id that appears on no title bar
          // defeats it.
          description: `Close one first — ${selection.cameraWindows
            .map((w) => cameraWindowName(w, resolveCameraWindowCallsign(w)))
            .join(' · ')}`,
        });
        return;
      }
      allocateCascadeSlot(target);
      dispatchSelection(
        target.kind === 'drone'
          ? {
              type: 'POP_OUT_VIDEO',
              droneId: target.droneId,
              cameraId: target.cameraId,
              callsign: target.callsign,
            }
          : {
              type: 'OPEN_DOCK_CAMERA',
              dockId: target.dockId,
              cam: target.cam,
              callsign: target.callsign,
            },
      );
    },
    [selection.cameraWindows, raiseCameraWindow, resolveCameraWindowCallsign, allocateCascadeSlot],
  );

  /** Pin a drone window - never unpin - on its first camera command (P1a). */
  const pinCameraWindow = useCallback((target: CameraWindow) => {
    dispatchSelection({ type: 'PIN_CAMERA_WINDOW', window: target });
  }, []);

  /** Flip whether a drone window survives the next map click. */
  const toggleCameraWindowPin = useCallback((target: CameraWindow) => {
    dispatchSelection({ type: 'TOGGLE_CAMERA_WINDOW_PIN', window: target });
  }, []);

  const closeCameraWindow = useCallback((target: CameraWindow) => {
    dispatchSelection(
      target.kind === 'drone'
        ? { type: 'DOCK_VIDEO', droneId: target.droneId, cameraId: target.cameraId }
        : { type: 'CLOSE_DOCK_CAMERA', dockId: target.dockId, cam: target.cam },
    );
  }, []);

  /** The dock sidebar's camera keys. The dock id is read here, at the press, and
   *  stored on the window — the sidebar never learns it, because the window has
   *  to keep pointing at this dock after the operator selects another. */
  const openDockCamera = useCallback(
    (cam: DockCameraSlot) => {
      const dockId = selection.selectedDockId;
      if (dockId === null) return;
      // The name is snapshotted at the press, so the window can still say whose
      // cameras it is showing if the dock registry stops answering later.
      openCameraWindow({
        kind: 'dock',
        dockId,
        cam,
        callsign: resolveDockCameraSource(dockId)?.callsign ?? null,
      });
    },
    [selection.selectedDockId, openCameraWindow, resolveDockCameraSource],
  );

  /**
   * Arms or disarms "fly here" pick mode, seeding the altitude on the way in.
   *
   * The seed is the aircraft's CURRENT altitude, not the org default:
   * repositioning a drone cruising at 120 m must not silently descend it to a
   * 76 m "standard". Only on the ground is there nothing to hold, and there the
   * org default is the sensible start — which is what replaces the magic 30 m
   * this flow used to substitute.
   */
  const handleToggleGoto = useCallback(() => {
    if (!gotoArmed) {
      const current = selectedDrone?.altitude ?? 0;
      const seed = current > GOTO_MIN_ALTITUDE_M ? current : altitude.profile.default_m;
      // Clamped against the GO-TO floor, not the takeoff floor: an aircraft
      // hovering at 4 m must keep holding 4 m, not be handed a 10 m climb it
      // was never asked for. The control widens its own band to cover whatever
      // lands here, so what is displayed is always what gets sent.
      setGotoAltitudeM(
        Math.min(ALTITUDE_HARD_BOUNDS[1], Math.max(GOTO_MIN_ALTITUDE_M, Math.round(seed * 10) / 10)),
      );
      setGotoUnit(altitude.profile.unit);
    }
    if (gotoArmed) disarmMapMode();
    else armMapMode({ kind: 'goto' });
  }, [gotoArmed, selectedDrone?.altitude, altitude.profile, armMapMode, disarmMapMode]);

  const selectedDockStatus: LiveDockStatus | null = selection.selectedDockId
    ? (dockMap.get(selection.selectedDockId) ?? null)
    : null;

  const selectedRegistryDock: Dock | null = selection.selectedDockId
    ? (dockRegistry.find((d) => d.id === selection.selectedDockId) ?? null)
    : null;

  const planHome = useMemo<LatLng | null>(
    () =>
      selectedRegistryDock &&
      selectedRegistryDock.latitude !== null &&
      selectedRegistryDock.longitude !== null
        ? { lat: selectedRegistryDock.latitude, lng: selectedRegistryDock.longitude }
        : null,
    [selectedRegistryDock],
  );

  const keepInFences = useMemo<KeepInFence[]>(
    () =>
      copGeofences
        .filter((g) => g.type === 'operational' && g.is_active !== false)
        .map((g) => ({ id: g.id, name: g.name, geometry: g.geometry })),
    [copGeofences],
  );

  /**
   * The dock planner asking for (or releasing) the map.
   *
   * Release is kind-scoped: a plan that finishes must not cancel a zone draw
   * the operator armed after it. Arming goes through `armMapMode`, so it
   * stands down whatever held the map before.
   */
  const handleDockPlanArmedChange = useCallback(
    (armed: boolean) => {
      if (armed) armMapMode({ kind: 'dock-plan' });
      else dispatchMapMode({ type: 'DISARM_KIND', kind: 'dock-plan' });
    },
    [armMapMode],
  );

  const missionPlan = useDockMissionPlan({
    dockId: selection.selectedDockId,
    home: planHome,
    keepInFences,
    armed: canDispatch && selection.selectedDockId !== null && mapMode.kind === 'dock-plan',
    onArmedChange: handleDockPlanArmedChange,
  });

  const planMapWaypoints = useMemo<PlanRoutePoint[]>(
    () =>
      missionPlan.waypoints.map((w, i) => ({
        lat: w.lat,
        lng: w.lng,
        invalid: missionPlan.invalidIndices.has(i),
      })),
    [missionPlan.waypoints, missionPlan.invalidIndices],
  );

  /**
   * A mode may not outlive the thing that armed it.
   *
   * The dock planner could only ever hold the map while a dock was selected and
   * the operator could dispatch — that used to be re-derived on every render
   * (`canDispatch && selectedDockId !== null && planOnMap`). With one owned
   * value it has to be released explicitly instead, or a click would still be
   * read as a waypoint after the dock sheet closed.
   */
  useEffect(() => {
    if (!canDispatch || selection.selectedDockId === null) {
      dispatchMapMode({ type: 'DISARM_KIND', kind: 'dock-plan' });
    }
  }, [canDispatch, selection.selectedDockId]);

  // ——— Composing a mission on the live map (task 3.2) ————————————————————
  //
  // The route being drawn shares the map's plan overlay with the dock planner —
  // one overlay, one set of gestures, whichever mode is armed. Which of the two
  // feeds it is decided once, below, from the mode.
  const composeArmed = mapMode.kind === 'mission-compose';
  /**
   * Compose is OPEN until the operator finishes with it, armed or not: point
   * entry ends on a double-click, but the drawn route and the panel stay — the
   * mission still has to be committed. Gating the panel on "has waypoints"
   * instead would make a compose the operator has not drawn into vanish the
   * moment they double-clicked.
   */
  const [composeOpen, setComposeOpen] = useState(false);
  /** The mission whose route is being revised on the map, or null. */
  const [editing, setEditing] = useState<EditingMission | null>(null);
  /**
   * The mission altitude comes back from the compose form; the route lives here.
   *
   * Nothing else does. The site the operator picks is metadata on the mission
   * record, not a launch point, so it never reaches the map or the route's
   * validation — picking or switching one leaves the drawn route untouched.
   */
  const [composeAltitudeM, setComposeAltitudeM] = useState(CANVAS_ALT_DEFAULT_M);
  /**
   * 022. The compose type lives HERE rather than inside the panel because two things outside
   * the panel depend on it: how many points the route may hold, and which geometry the map is
   * armed for. The panel owns the control; the page owns the consequences.
   */
  const [composeType, setComposeType] = useState<MissionType>(DEFAULT_MISSION_TYPE);
  /** Drives the orbit ring on the map. Null whenever there is no usable radius to draw. */
  const [composeOrbitRadiusM, setComposeOrbitRadiusM] = useState<number | null>(null);
  const composeRoute = useDrawnRoute({
    altitudeM: composeAltitudeM,
    keepInFences,
    maxWaypoints: waypointCountRangeForType(composeType).max,
  });
  composeRouteRef.current = composeRoute;

  /**
   * A type change re-arms the map for the new geometry and drops points the new type cannot
   * hold. The panel has already confirmed with the operator when that would throw work away.
   */
  const handleComposeTypeChange = useCallback((next: MissionType) => {
    setComposeType(next);
    const max = waypointCountRangeForType(next).max;
    const current = composeRouteRef.current;
    if (current && current.waypoints.length > max) {
      // Keep the points the new type CAN hold rather than clearing outright — switching a
      // five-point route to a GO TO POINT most likely means "this first point", and throwing
      // away a placement the operator can still use is work destroyed for nothing.
      current.load(current.waypoints.slice(0, max));
    }
    armMapMode({ kind: 'mission-compose', shape: geometryShapeForType(next) });
  }, [armMapMode]);

  /**
   * The route being revised. A second instance rather than a shared one: compose
   * and edit are different work, and one operator's half-drawn new mission must
   * not be silently overwritten by opening an existing route.
   */
  const editRoute = useDrawnRoute({
    altitudeM: editing?.altitudeM ?? CANVAS_ALT_DEFAULT_M,
    keepInFences,
    // 022. The revised mission's own type caps the route, exactly as it does in compose.
    // Without this, revising an ORBIT appended a second point instead of moving the centre —
    // the banner said "click to move the orbit centre", the ring disappeared (it needs
    // exactly one point) and the save was then blocked by a route the map had just built.
    maxWaypoints: editing ? waypointCountRangeForType(editing.type).max : MAX_ROUTE_WAYPOINTS,
  });
  editRouteRef.current = editRoute;
  const editDirty = editing !== null
    && drawnRouteSignature(editRoute.waypoints, editing.altitudeM) !== editing.storedSignature;
  composeOpenRef.current = composeOpen;
  editDirtyRef.current = editDirty;
  editingIdRef.current = editing?.id ?? null;

  const planEditable = isDockPlanEditable(
    canDispatch, selection.selectedDockId, selection.launchIntent, missionPlan.waypoints.length,
  );
  // The compose count is what makes the banner honest about the double-click on
  // an empty route; the other modes ignore it.
  const planModeLabel = mapModeBannerLabel(
    mapMode,
    missionPlan.planningKind,
    // Whatever the DESCRIBED mode has placed so far - compose into the draft
    // route, the measurement tool into its open line. `bannerDrawnCount` owns
    // which, so the rule is pinned over every variant instead of living in a
    // ternary nothing tests.
    bannerDrawnCount(mapMode, {
      plan: composeRoute.waypoints.length,
      measure: openLinePointCount(rangeBearingMeasurements),
    }),
  );

  // Everything a mode owns outside the map is released HERE, on the way out, so
  // no caller can arm something else and leave a half-finished workflow behind.
  standDownModeRef.current = (prev, next) => {
    // The rule itself is `standDownEffects` in `lib/map-mode.ts`, not inline
    // here. It is the only place that can undo D10, and while it lived in this
    // component nothing could test it - the first D10 regression harness had to
    // hand-write its own copy and so would have passed with the bug put back.
    const effects = standDownEffects(prev, next);
    if (effects.clearMeasurement) setRangeBearingMeasurements(NO_MEASUREMENTS);
    if (prev.kind === next.kind) return;
    if (effects.finishDockPlanning) {
      // Ends point entry AND clears planningKind. Without the second part the
      // launch overlay's re-arm effect would take the map straight back off
      // whatever the operator just armed.
      missionPlan.finishPlanning();
    }
  };

  /**
   * Escape stands the armed mode down before anything else acts on the key.
   *
   * Capture phase for exactly that reason: an armed map click is the most
   * consequential thing on the screen, so backing out of it must not depend on
   * which panel happens to have focus. Guarded like every other Escape handler
   * here — a dialog owns its own Escape, a tooltip owns its own Escape, and so
   * does a field being typed in.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isMapModeArmed(mapModeRef.current)) return;
      // An open tooltip takes the first Escape; the map takes the next one.
      //
      // Both this and the tooltip's own handler sit on `window` in capture, and this one is
      // registered first, so `stopPropagation()` cannot keep the tooltip from acting — both
      // ran, and one Escape closed the tooltip AND un-armed waypoint placement. Compose now has
      // a re-arm control (#641), so that is no longer unrecoverable — but it is still two
      // effects from one key. Dismissing a tooltip is the gesture WCAG 1.4.13 requires of
      // anything shown on hover, so it must not also throw away the armed mode. Queried from
      // the DOM rather than shared through state because the check has to hold for any panel
      // that grows a tooltip later, not just compose.
      if (document.querySelector('[role="alertdialog"], [role="dialog"], [role="tooltip"]')) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
        return;
      }
      e.stopPropagation();
      disarmMapMode();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [disarmMapMode]);

  const { bays: dockBays, error: dockBaysError } = useDockBays(selection.selectedDockId);
  const operationalPhase = useDockOperationalPhase(selection.selectedDockId, subscribeDockEvent);

  // The launch workflow acts on the drone picked in a bay row; with none picked
  // it falls back to the dock's own launch-bay drone, as before.
  const launchTargetDroneId = selection.launchIntent?.droneId ?? null;
  const assignedDroneId = launchTargetDroneId ?? selectedRegistryDock?.assigned_drone_id ?? null;
  const assignedDrone = useMemo<LiveDrone | null>(
    () => (assignedDroneId ? droneMap.get(assignedDroneId) ?? null : null),
    [assignedDroneId, droneMap],
  );
  // A CURRENT flying claim, not the frozen last status - a publisher that died mid-sortie must not
  // keep the active-waypoint highlight marching on stale `mission_current`. Render-body clock:
  // converges on the next inbound frame from any drone (see `isCurrentlyFlying`).
  const missionExecuting = assignedDrone !== null && isCurrentlyFlying(assignedDrone);
  const planActiveIndex = useMemo<number | null>(
    () =>
      missionExecuting
        ? activeWaypointIndex(assignedDrone?.mission_current, missionPlan.waypoints.length)
        : null,
    [missionExecuting, assignedDrone?.mission_current, missionPlan.waypoints.length],
  );

  /**
   * A click that would leave an aircraft under manual authority is a proposal,
   * not a navigation.
   *
   * `useManualControl` abandons the session whenever its `droneId` changes, so
   * a background press on the map, a press on another aircraft or a press on a
   * dock all handed the aircraft back mid-flight with no warning. While
   * authority is held those actions are parked here until the operator answers
   * `ManualExitConfirm`; everything else dispatches untouched, and no
   * command-bar verb goes through this path at all — RTH, LAND and ABORT stay
   * available in every state, which is a non-negotiable of the feature.
   *
   * `after` carries the side effect the caller wanted to run alongside the
   * selection (the dock roster flies the camera), so a cancelled exit does not
   * leave the camera somewhere the operator did not agree to go.
   */
  const [pendingSelection, setPendingSelection] = useState<{
    action: SelectionAction;
    after?: () => void;
  } | null>(null);

  const aimOwnsControlRef = useRef(false);

  const dispatchGuardedSelection = (action: SelectionAction, after?: () => void) => {
    // An aim session is not "flying this aircraft": asking an operator to confirm leaving it,
    // because they pressed a camera key a moment ago, is a dialog about a flight nobody started.
    if (
      manualRef.current?.holdsAuthority
      && !aimOwnsControlRef.current
      && leavesFlownAircraft(action, activeSelectedId)
    ) {
      setPendingSelection({ action, after });
      return;
    }
    dispatchSelection(action);
    after?.();
  };

  /**
   * WHO IS DRIVING THE PLAN OVERLAY.
   *
   * The map has exactly one route-drawing overlay, and the dock launch planner
   * and mission compose both use it — click adds, drag moves, double-click ends.
   * Which one it shows is decided here, from the mode, once. Deriving it at each
   * of the eight props instead is how the overlay ends up showing one route
   * while the clicks land in the other.
   */
  const composeMapWaypoints = useMemo<PlanRoutePoint[]>(
    () =>
      composeRoute.waypoints.map((w, i) => ({
        lat: w.lat,
        lng: w.lng,
        invalid: composeRoute.invalidIndices.has(i),
      })),
    [composeRoute.waypoints, composeRoute.invalidIndices],
  );

  const editArmed = mapMode.kind === 'mission-edit';

  const editMapWaypoints = useMemo<PlanRoutePoint[]>(
    () =>
      editRoute.waypoints.map((w, i) => ({
        lat: w.lat,
        lng: w.lng,
        invalid: editRoute.invalidIndices.has(i),
      })),
    [editRoute.waypoints, editRoute.invalidIndices],
  );

  const planOverlay = editing
    ? {
        waypoints: editMapWaypoints,
        // 022. An orbit's ring is not drawn while REVISING one yet — the edit panel has no
        // radius control, so there is no value to draw it at, and a ring at the stored radius
        // would sit unchangeably beside a centre the operator is moving.
        orbitRadiusM: null as number | null,
        home: null,
        editable: editArmed,
        selectedIndex: editRoute.selectedIndex,
        // The mission may be scheduled, but nothing is flying THIS route yet —
        // an executing one never reaches this panel.
        activeIndex: null as number | null,
        onMapClick: (lat: number, lng: number) => {
          if (!editRoute.addFromMap(lat, lng)) {
            toast.error(`A route can hold at most ${MAX_ROUTE_WAYPOINTS} waypoints`);
          }
        },
        onWaypointClick: editRoute.select,
        onWaypointDrag: editRoute.dragTo,
        onModeExit: () => dispatchMapMode({ type: 'DISARM_KIND', kind: 'mission-edit' }),
      }
    : composeOpen
    ? {
        waypoints: composeMapWaypoints,
        // 022. Drives the orbit ring. Null for every type but ORBIT, and while the radius box
        // holds nothing usable, so the ring appears with the type and leaves with it.
        orbitRadiusM: composeOrbitRadiusM,
        // No launch point. A composed route starts at WP1: the chosen site is
        // metadata on the mission, not a takeoff pad, so it gets no 'H' and no
        // leading leg. Only the dock branch below has a real one.
        home: null,
        editable: composeArmed,
        selectedIndex: composeRoute.selectedIndex,
        // Nothing is flying a route that has not been created yet.
        activeIndex: null as number | null,
        onMapClick: (lat: number, lng: number) => {
          if (!composeRoute.addFromMap(lat, lng)) {
            toast.error(`A route can hold at most ${MAX_ROUTE_WAYPOINTS} waypoints`);
          }
        },
        onWaypointClick: composeRoute.select,
        onWaypointDrag: composeRoute.dragTo,
        // Double-click ends point entry but keeps the drawn route and the panel:
        // the operator still has to commit it, and throwing the route away on a
        // gesture that means "done placing" would destroy the work.
        onModeExit: () => dispatchMapMode({ type: 'DISARM_KIND', kind: 'mission-compose' }),
      }
    : {
        waypoints: planMapWaypoints,
        // 022. A dock launch plan has no mission type and therefore never an orbit. Stated
        // rather than left off: all three branches of this expression are read through one
        // `planOverlay.orbitRadiusM`, and an absent key there would reach the map as
        // undefined instead of failing here.
        orbitRadiusM: null as number | null,
        home: planHome,
        editable: planEditable,
        selectedIndex: missionPlan.selectedIndex,
        activeIndex: planActiveIndex,
        onMapClick: missionPlan.addFromMap,
        onWaypointClick: missionPlan.select,
        onWaypointDrag: missionPlan.dragTo,
        onModeExit: missionPlan.stopPlanOnMap,
      };

  /**
   * Clicking an aircraft on the map opens its picture over the map.
   *
   * Every side effect here runs through the guard's `after` hook rather than
   * before the dispatch (#279), so a manual-exit the operator CANCELS leaves the
   * mission picture — and now the camera windows — exactly as they were. The
   * guard's whole premise is that a parked click has had no effect yet.
   *
   * Gated on `source === 'map-pick'`, and that gate is load-bearing. This
   * handler is NOT the map click alone: `CopMapStage` hands it to the map AND
   * to the StripBoard in the left column, and inside the map it is also what
   * the range-rings menu item calls to select before it can draw, and what the
   * camera menu item calls before opening its own window. Opening a picture on
   * every one of those means a strip read, or a request for range rings,
   * answers with a video window nobody asked for. Only a press on the aircraft
   * ON the tactical picture is a request to see what it sees.
   *
   * Silent about two things on purpose:
   *  - the aircraft may have no camera. The window says so in its own words,
   *    which is a better answer than a click that appears to do nothing.
   *  - the stage may already be drawing this aircraft's picture — a manual
   *    session, or the split beside the map. Then a window would be the same
   *    stream decoded twice, and for an aircraft whose single camera is unnamed
   *    it is worse than that: the popped set would take that camera off the
   *    stage, so the click would MOVE the picture out of the big view the
   *    operator is flying on. The stage already shows what the window would.
   */
  const handleSelectDrone = (id: string, source?: DroneSelectSource) =>
    dispatchGuardedSelection({ type: 'SELECT', id }, () => {
      // The empty-map click is the universal "clear" gesture (DroneMap calls
      // this with '' for a background click): the mission pick goes with the
      // drone selection, so a dimmed route picture never outlives its reason.
      if (!id) {
        setSelectedMissionId(null);
        return;
      }
      if (source !== 'map-pick') return;
      if (stagePlacement !== null) return;
      const follow: CameraWindow = {
        kind: 'drone',
        droneId: id,
        cameraId: SINGLE_CAMERA,
        // Snapshotted here, as every window's is: the live list is
        // site-filtered, so an aircraft can leave it while its window plays on.
        callsign: copDroneTracks.get(id)?.callsign?.trim() || null,
        pinned: false,
      };
      // The same set the reducer is about to keep, so this check and the open it
      // narrates cannot disagree about whether there is room.
      const kept = dropTransientWindows(selection.cameraWindows, id);
      const alreadyOpen = kept.some((w) => w.kind === 'drone' && w.droneId === id);
      if (!alreadyOpen && cameraWindowsFull(kept)) {
        // Said, not shouted. `toast.error` is for a thing the operator ASKED
        // for that failed — FEED's key, where they pressed something named
        // after a window. Here the window is a consequence of picking an
        // aircraft: the pick itself succeeded, and reporting a navigation
        // gesture as an error, in the palette's failure colour, on every
        // subsequent aircraft they touch, teaches them to ignore red.
        //
        // Named rather than counted, for the same reason the pop-out refusal
        // is: windows can be dragged anywhere on the map, so "four are open"
        // leaves the operator hunting for them. One toast id, because this
        // fires off a pick rather than a press — clicking through a four-ship
        // must not stack four identical notices.
        toast(`${MAX_CAMERA_WINDOWS} camera windows already open`, {
          id: 'camera-windows-full',
          // "close one" first, because unpinning alone changes nothing until
          // the NEXT pick — advice that does not work when followed is worse
          // than none.
          description: `Close one to see this camera — ${kept
            .map((w) => cameraWindowName(w, resolveCameraWindowCallsign(w)))
            .join(' · ')}`,
        });
        return;
      }
      // A window already on this aircraft is raised, not reopened, so it keeps
      // the cascade step it already holds.
      if (!alreadyOpen) allocateCascadeSlot(follow, kept);
      dispatchSelection({
        type: 'FOLLOW_SELECTION_CAMERA',
        droneId: id,
        callsign: follow.callsign,
      });
    });
  const handleSelectDock = (id: string) =>
    openSheetFromOutside(() => dispatchGuardedSelection({ type: 'SELECT_DOCK', id }));

  // Leaving a launch workflow must also drop the half-drawn route from the map.
  const handleCancelLaunch = () => {
    missionPlan.clear();
    dispatchSelection({ type: 'CANCEL_LAUNCH' });
  };
  const handleSelectBayDrone = (droneId: string | null) => {
    if (selection.launchIntent && selection.launchIntent.droneId !== droneId) missionPlan.clear();
    dispatchSelection({ type: 'SELECT_BAY_DRONE', droneId });
  };
  const handleLaunch = (droneId: string, mode: LaunchMode) => {
    missionPlan.clear();
    dispatchSelection({ type: 'START_LAUNCH', droneId, mode });
  };

  const handleSelectDockFromRoster = (id: string) => {
    const dock = dockMarkers.find((d) => d.id === id);
    openSheetFromOutside(() => dispatchGuardedSelection({ type: 'SELECT_DOCK', id }, () => {
      if (dock) {
        window.dispatchEvent(new CustomEvent('flyToSite', { detail: { lat: dock.latitude, lng: dock.longitude } }));
      }
    }));
  };

  const handleFlyToDrone = (droneId: string) => {
    const drone = drones.find((d) => d.drone_id === droneId);
    if (drone) {
      window.dispatchEvent(
        new CustomEvent('flyToDrone', { detail: { lat: drone.latitude, lng: drone.longitude } }),
      );
    }
    dispatchGuardedSelection({ type: 'SELECT', id: droneId });
  };

  // Pure map centering: the dock sheet's go-to-drone button, and the missions
  // panel's center-on-aircraft control. Deliberately NOT handleFlyToDrone:
  // SELECT replaces whatever context the sheet is holding (copReducers), which
  // would close the sheet the operator is working in, destroy its floating
  // camera windows mid-sortie, and — from the mission queue — swap the panel
  // for the DRONE tab. The camera moves; nothing else does.
  const handleCenterOnDrone = (droneId: string) => {
    const drone = drones.find((d) => d.drone_id === droneId);
    if (drone) {
      window.dispatchEvent(
        new CustomEvent('flyToDrone', { detail: { lat: drone.latitude, lng: drone.longitude } }),
      );
    }
  };

  // Registry row for the selected drone — the airframe facts telemetry omits,
  // and the link to its home dock.
  const selectedDroneRow = activeSelectedId ? droneRegistry.get(activeSelectedId) ?? null : null;
  const selectedDroneHomeDock = selectedDroneRow?.dock_id
    ? dockRegistry.find((d) => d.id === selectedDroneRow.dock_id) ?? null
    : null;

  // Which executor the API will route this drone's commands to. Resolved
  // server-side and read through one helper — see commandRouteOf for why the COP
  // must not re-derive the vendor rule itself.
  const selectedCommandRoute = commandRouteOf(selectedDroneRow);
  // #261: whether the RC app can sign at all. Read from the same registry row
  // as the route, so the bar cannot hold one without the other.
  const selectedCommandSecretPresent = commandSecretPresentOf(selectedDroneRow);

  // Companion-app liveness (T4). The live map wins when a heartbeat has arrived
  // on this connection; before that the registry row carries the last known
  // state, so a screen opened onto an already-dead app says so immediately.
  // Null for every aircraft with no companion — those show no row at all.
  const selectedDroneCompanion = activeSelectedId
    ? companionMap.get(activeSelectedId) ?? selectedDroneRow?.companion ?? null
    : null;

  // The same two sources, for the coverage block's relief (#810): whether the RC companion app a
  // parked relief will be launched through is there. A lookup, because the block knows which
  // aircraft is the relief and this page does not.
  const reliefLinkOf = useCallback((droneId: string) => {
    const row = droneRegistry.get(droneId) ?? null;
    return reliefLinkStatus(commandRouteOf(row), companionMap.get(droneId) ?? row?.companion ?? null);
  }, [droneRegistry, companionMap]);

  const selectedDroneMission = useMemo(
    () => (activeSelectedId ? resolveDroneMission(activeMissions, activeSelectedId, launchedMissions) : null),
    [activeMissions, activeSelectedId, launchedMissions],
  );

  /**
   * Gimbal commands LOOK HERE has sent. The picture holds a tapped tilt until its aircraft turn
   * goes (D32 in docs/PLANS/aircraft-yaw-camera-keys/IMPLEMENTATION-TILT-WITH-TURN.md), and a
   * LOOK HERE made in that wait is a newer intent the held tilt must not undo. LOOK HERE does
   * not go through the picture's own aim hook, so this is how the picture hears of it.
   */
  const [lookHereGimbalSends, setLookHereGimbalSends] = useState(0);

  /**
   * Camera control (issue #244), built ONCE and given to every surface that
   * draws this aircraft's picture: the FEED segment inline, the popped-out
   * window, and the manual-flight stage.
   *
   * One object rather than three prop lists, because the gates are the whole
   * point — the same route/role/liveness checks as the flight command bar
   * (RemoteID, link freshness, companion liveness) plus the payload
   * capabilities from the heartbeat. Three surfaces assembling their own inputs
   * is three chances for one of them to offer a control the other two refuse,
   * and an operator who is told "no" in the panel and "yes" in the window has
   * learned nothing about the aircraft.
   *
   * The picture is only ever drawn in ONE of those places at a time, so this
   * does not create competing control surfaces — see CameraControlOverlay.
   */
  const cameraControl: CameraControlInputs | undefined = selectedDrone
    ? {
        commandRoute: selectedCommandRoute,
        payloadCapabilities: selectedDroneCompanion?.payload_capabilities ?? null,
        gimbalPitchDeg: selectedDrone.gimbal_pitch,
        gimbalYawDeg: selectedDrone.gimbal_yaw,
        zoomRatio: selectedDrone.zoom_ratio,
        cameraLens: selectedDrone.camera_lens ?? null,
        headingDeg: selectedDrone.heading,
        operator: user?.email ?? null,
        canOperate: canDispatch,
        telemetrySource: selectedDrone.telemetry_source,
        lastSeenAt: selectedDrone.last_seen_at,
        companion: selectedDroneCompanion,
        onCameraCommand: (action, params) => {
          // Answered BEFORE the send and returned: the #344 lock drops a second
          // gimbal command while the first is in flight, and the overlay's aim
          // target must not record a command that never left (P1a, finding E1).
          if (!droneCommand.wouldSend(action, params)) return false;
          // LOOK HERE measures its relative tilt from telemetry, so it has to
          // know a gimbal command left for this aircraft - stamped here, before
          // the POST, because a POST that answers after the selection moved is
          // never recorded anywhere else (useLookHere, review S2).
          if (action === 'gimbal') lookHere.noteGimbalCommand(selectedDrone.drone_id);
          // Remember the lens ASKED FOR before the send, not after it resolves:
          // the pending state is exactly the window between the press and the
          // camera's readback, so recording it on success would mean it is never
          // shown, and recording it on ack would leave the press unacknowledged
          // for as long as the RC takes to answer.
          if (action === 'lens' && params?.lens) {
            setRequestedLens(params.lens);
          }
          void droneCommand.send(action, params);
          return true;
        },
        gimbalPhase: droneCommand.phaseOf('gimbal'),
        gimbalAckId: droneCommand.ackIdOf('gimbal'),
        gimbalSendsElsewhere: lookHereGimbalSends,
        zoomPhase: droneCommand.phaseOf('zoom'),
        lensPhase: droneCommand.phaseOf('lens'),
        gimbalFailure: droneCommand.failureOf('gimbal'),
        zoomFailure: droneCommand.failureOf('zoom'),
        lensFailure: droneCommand.failureOf('lens'),
        requestedLens,
      }
    : undefined;

  // LOOK HERE (video/gimbal P2): the map's way to point the selected aircraft's
  // camera. Gated by the same camera ladder the window above uses, sent through
  // the same #344 lock - see useLookHere.
  const lookHereCamera = useMemo<LookHereCameraInputs | null>(
    () =>
      selectedDrone
        ? {
            commandRoute: selectedCommandRoute,
            capabilities: selectedDroneCompanion?.payload_capabilities ?? null,
            canOperate: canDispatch,
            telemetrySource: selectedDrone.telemetry_source,
            lastSeenAt: selectedDrone.last_seen_at,
            companion: selectedDroneCompanion,
          }
        : null,
    [selectedDrone, selectedCommandRoute, selectedDroneCompanion, canDispatch],
  );
  const lookHereCallsign = selectedDrone?.callsign?.trim() || 'the selected aircraft';
  const armLookHere = useCallback(
    () => armMapMode({ kind: 'look-here', callsign: lookHereCallsign }),
    [armMapMode, lookHereCallsign],
  );
  const disarmLookHere = useCallback(() => {
    if (mapModeRef.current.kind === 'look-here') disarmMapMode();
  }, [disarmMapMode]);
  const lookHere = useLookHere({
    drone: selectedDrone ?? null,
    camera: lookHereCamera,
    armed: mapMode.kind === 'look-here',
    arm: armLookHere,
    disarm: disarmLookHere,
    sendTilt: (pitchDeltaDeg) => {
      const params = { pitch_deg: pitchDeltaDeg };
      if (!droneCommand.wouldSend('gimbal', params)) return false;
      setLookHereGimbalSends((n) => n + 1);
      return droneCommand.send('gimbal', params);
    },
    gimbalAckId: droneCommand.ackIdOf('gimbal'),
    gimbalPhase: droneCommand.phaseOf('gimbal'),
    gimbalStatus: droneCommand.statusOf('gimbal'),
    gimbalFailure: droneCommand.failureOf('gimbal'),
  });
  // No LOOK key at all where it could never be used - a viewer, a RemoteID
  // broadcast, an aircraft not commanded through the companion. A permanently
  // dead command key on every selection teaches operators to ignore dimmed
  // keys (slice 3 review); the dimmed-but-pressable key is for reasons that
  // pass (on the ground, tilt not reported, link).
  const lookHereOffered =
    selectedDrone != null &&
    canDispatch &&
    selectedCommandRoute === 'dji_companion' &&
    selectedDrone.telemetry_source !== 'remoteid';

  // ---------------------------------------------------------------------------
  // Manual flight control — DJI companion aircraft only, airborne only.
  //
  // The refusal is computed here rather than inside the bar because it needs the
  // registry row, the companion heartbeat and the role all at once, and every
  // string it can produce is fixed by the wire contract so the operator reads the
  // same sentence whether the browser, the API or the RC refused.
  //
  // `surfaceControllable` is answered from a REAL MEASUREMENT of the map column
  // (`mapBox`), fed through the same `ringGeometry` the control surface itself
  // uses — so the MANUAL key and the surface can never disagree about whether
  // there is room to fly. Without it the key was pressable into a surface that
  // then rendered display-only, which is a promise the COP could not keep.
  // ---------------------------------------------------------------------------
  /**
   * The UNCOVERED map, measured.
   *
   * `ManualStage` attaches this to an inert box sized to whatever the floating
   * chrome leaves, so this is already the answer rather than a canvas that has to
   * be corrected. In the classic shell nothing floats and the box is the map.
   *
   * It is measured rather than derived because the correction is not the same for
   * every box inside the stage: the VIDEO layout's control minimap is lifted
   * above the bottom stack, and a subtraction performed here would have shrunk it
   * by a stack that is not over it.
   */
  const { ref: mapBoxRef, size: mapBoxSize } = useMeasuredSize<HTMLDivElement>();

  const stageInsetsController = useStageInsetsController();

  /**
   * The navigation rail, folded into the same registry as everything else.
   *
   * It is the one surface that covers the stage from OUTSIDE it: the dashboard
   * layout renders it above every route, so it cannot reach this provider the
   * way `CopBottomStack` and `CopSidePanel` do. It measures its own resting box
   * and publishes the number through `DashboardShellProvider`; this hands that
   * number to the registry, so the manual-flight geometry reads one combined
   * answer rather than reconciling two sources.
   *
   * Registered here rather than through `useStageInsetHost` because this
   * component OWNS the provider and cannot consume its own context. Zero when
   * the rail is in flow, which deregisters it.
   */
  const { railInsetPx, setManualFlightEngaged } = useDashboardShell();
  const registerStageInset = stageInsetsController.register;
  useEffect(() => {
    if (railInsetPx <= 0) {
      registerStageInset(RAIL_INSET_ID, null);
      return;
    }
    registerStageInset(RAIL_INSET_ID, { edge: 'left', sizePx: railInsetPx });
    return () => registerStageInset(RAIL_INSET_ID, null);
  }, [railInsetPx, registerStageInset]);

  /**
   * What the operator is holding — measured, never sniffed. It decides the
   * pointer mode every manual constant is keyed on (a 56 px halo on a finger,
   * 44 on a mouse), whether the device is refused outright (a phone, a browser
   * with no pointer capture), and whether the tablet is upright.
   */
  const manualDevice = useManualDevice();

  // The controller has to exist before the layout hook can cancel a drag on it,
  // and the layout has to be known before the controller can report it on
  // release. A ref breaks that cycle without either of them holding a stale
  // copy of the other.
  const manualRef = useRef<ManualControlController | null>(null);
  const cancelBeforeLayoutChange = useCallback(() => {
    manualRef.current?.hover();
  }, []);
  const { layout: manualLayout, setLayout: setManualLayout, persisted: manualLayoutPersisted } =
    useManualStageLayout({ operator: user?.email ?? null, onBeforeChange: cancelBeforeLayoutChange });
  // "Video beside the map" outside a session (#505 note 4). Per operator, like
  // the layout above, and deliberately a different key from it: choosing the
  // split here must never change what MANUAL opens with (split-video record,
  // D3 and D5).
  const {
    on: splitVideoOn,
    setOn: setSplitVideoOn,
    share: splitVideoShare,
    setShare: setSplitVideoShare,
  } = useSplitVideoPreference({
    operator: user?.email ?? null,
  });
  // The operator has asked for the split and it can be honoured on this device.
  // Whether the stage actually shows it also depends on the manual session,
  // which is decided further down (`splitVideoActive`); this half is what the
  // control-surface measurement below needs. Phones are excluded exactly as
  // manual flight excludes them: a 170 px map half is not a map (D10).
  const splitStageRequested =
    features.copSplitVideo && splitVideoOn && !!selectedDrone && manualDevice.deviceClass !== 'phone';
  // The whole stage, measured beside the map box: with the split on, the map box
  // is half the stage, but a granted session re-hosts the stage at
  // `manualLayout`, and for MAP and VIDEO that is the whole stage again.
  // Measuring the half would refuse MANUAL on a tablet for a surface the session
  // would never fly from (review finding, 2026-09-02; D11).
  const { ref: stageBoxRef, size: stageBoxSize } = useMeasuredSize<HTMLDivElement>();
  // And the map half a SPLIT session would fly from. The regular-mode split can
  // be dragged anywhere from 10% to 75% map, but a session's SPLIT is always
  // half and half, so the on-screen map box stopped being that half the moment
  // the divider moved (resize record, D3).
  const { ref: sessionSplitBoxRef, size: sessionSplitBoxSize } =
    useMeasuredSize<HTMLDivElement>();

  // Null means "not measured yet", which is not the same as "too small": the
  // first render has no box to read, and refusing on ignorance would flash a
  // disabled MANUAL key on every load. The surface itself is the backstop.
  // The pointer mode goes in because the minimum surface is 240 px on a mouse
  // and 280 on a finger — the key and the surface must not disagree.
  //
  // The refusal travels with the verdict so the MANUAL key can state the
  // MEASUREMENT ("Control surface is 210 px — manual flight needs 240"), which
  // an operator can act on by closing the details panel. "Needs a tablet or
  // larger" is a different refusal, about the device, and `useManualDevice`
  // owns it.
  // The box the session would FLY FROM. With the regular-mode split on and a
  // stored manual layout of MAP or VIDEO, that is the whole stage; only a stored
  // SPLIT flies from the half the map box measures today.
  const controlSurfaceSize = !splitStageRequested
    ? mapBoxSize
    : manualLayout === 'split'
      ? sessionSplitBoxSize
      : stageBoxSize;
  const mapBoxRing =
    controlSurfaceSize === null
      ? null
      : ringGeometry(controlSurfaceSize, { pointer: manualDevice.pointer });
  const surfaceControllable = mapBoxRing === null ? true : mapBoxRing.controllable;
  const surfaceRefusal = mapBoxRing?.refusal ?? null;

  /**
   * Sessions THIS console has held.
   *
   * `manual_control_state` is broadcast org-wide, so our own frames come back to
   * us on the same channel as everybody else's. Without this the COP would read
   * its own session as "somebody else has this aircraft" and refuse the operator
   * their own drone — the same dead MANUAL key, only more confusing.
   *
   * Declared here rather than beside the effect that fills it, because the
   * availability memo below reads it and is itself declared before the
   * controller that produces the ids.
   */
  const [ownManualSessions, setOwnManualSessions] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  /**
   * What the org-wide broadcast says about the SELECTED aircraft, if anything.
   *
   * This is the only signal a second console has that an aircraft is already
   * being flown by hand. Without it operator B sees a normal aircraft, presses
   * MANUAL, spends a round trip, and is refused by the authority lock — with two
   * consoles in one room and no way to tell that A is flying it.
   */
  const manualHold = activeSelectedId ? manualControlMap.get(activeSelectedId) ?? null : null;

  /**
   * What the API says was already true when this screen opened.
   *
   * The only path by which a COP that reloaded mid-session can find out that it
   * still holds the aircraft — the broadcast above carries a session id this
   * page load has never seen, so without this the operator's own session reads
   * back as a foreign hold and the MANUAL key is refused in their own name.
   */
  const manualSnapshot = useManualControlSnapshot(activeSelectedId);

  const manualAvailability = useMemo(
    () =>
      selectedDrone
        ? manualControlOffer({
            drone: selectedDrone,
            manufacturer: selectedDroneRow?.manufacturer,
            // The server's routing verdict, NOT integration_config.companion —
            // routing moved to the model in #188, and a Mavic registered through
            // the fleet UI (which cannot set integration_config at all) routes
            // dji_companion with no explicit flag. Re-deriving the vendor rule
            // here blocked the MANUAL key on an aircraft the backend was
            // granting sessions for.
            isCompanion: selectedCommandRoute === 'dji_companion',
            registryKnown: selectedDroneRow != null,
            // A signing secret the server says is absent. Not read from
            // integration_config (which never ships the secret) but from the
            // presence flag #313 added -- the same server verdict the flight
            // bar's dead-ARM gate reads. Without it the MANUAL key offered a
            // session the backend refuses outright.
            commandSecretPresent: selectedCommandSecretPresent,
            canOperate: canDispatch,
            companionOnline: selectedDroneCompanion?.is_online ?? null,
            commandLinkDown: hasNoCommandLink(selectedDroneCompanion),
            // Refused BEFORE the press, with the contract's own sentence, rather
            // than after a round trip that produced a toast and then vanished.
            // The signed-in account goes in so a session THIS operator holds is
            // never rendered as somebody else's: after a reload the session id
            // is unknown to this page load, and without the account the refusal
            // names the person reading it and locks them out of their own
            // aircraft for the lock's full TTL.
            heldBy: manualHoldRefusal({
              hold: manualHold,
              ownSessionIds: ownManualSessions,
              selfOperator: user?.email ?? null,
            }),
            surfaceControllable,
            surfaceRefusal,
            // A portrait surface has no control layout, so authority must never
            // be taken on one: the operator would lock the person standing next
            // to the aircraft out of the sticks and then be handed a rotate
            // prompt instead of a control.
            portrait: manualDevice.portrait,
            pointer: manualDevice.pointer,
            deviceRefusal: manualDevice.refusal,
            // Manual flight is not released (`MANUAL_FLIGHT_RELEASED`), and the
            // one thing that must still get through that switch is an aircraft
            // THIS console already holds on the server. After a reload whose
            // re-adoption is refused, `holdsAuthority` is false and pressing
            // MANUAL to take a clean lock is the only way back into a session
            // that is still ours — so the offer has to stay open for it.
            // `user_id`, not the display name: two accounts can share a name.
            heldBySelf:
              manualSnapshot?.holder != null &&
              user?.id != null &&
              manualSnapshot.holder.user_id === user.id,
          })
        : { available: false, reason: 'Select an aircraft first.' },
    // Recomputed on every telemetry frame, because `selectedDrone` is replaced
    // by each one. A drone that stops reporting therefore freezes this verdict —
    // which is why the API re-checks staleness on `request` and answers with the
    // identical string rather than trusting what the browser decided. That same
    // 1 Hz recomputation is what ages a hold out: `manualHoldRefusal` compares
    // the last frame against the clock, and its own identity does not change
    // when the frames simply stop.
    [
      selectedDrone,
      selectedDroneRow,
      selectedDroneCompanion,
      canDispatch,
      manualHold,
      ownManualSessions,
      user?.email,
      user?.id,
      manualSnapshot,
      surfaceControllable,
      surfaceRefusal,
      manualDevice.portrait,
      manualDevice.pointer,
      manualDevice.refusal,
    ],
  );

  /**
   * Aircraft with a mission executing right now.
   *
   * The RC refuses a manual session outright while a mission runs, so this is what lets the
   * camera keys say so on the surface instead of costing a press and up to 3 s to be refused.
   */
  const liveMissionDroneIds = useMemo(
    () =>
      new Set(
        missionsQueue.missions
          .filter((m) => m.status === 'in_progress' && m.assigned_drone_id)
          .map((m) => m.assigned_drone_id as string),
      ),
    [missionsQueue.missions],
  );

  /**
   * What the COP offers for AIRCRAFT YAW from the camera picture (#732).
   *
   * Beside `manualAvailability` rather than derived from it, because the two diverge on purpose:
   * the manual ladder ends in device, portrait and surface-size refusals that exist because the
   * drag RING needs room to drag in, and two 40 px keys do not. It adds a mission refusal the
   * manual ladder has no need for.
   *
   * `heldBy` goes through the SAME `manualHoldRefusal` derivation, which is load-bearing rather
   * than tidy: a raw hold refuses on our OWN session, so every turn would be refused at dispatch
   * the moment this control acquired one.
   */
  const aircraftYaw = useMemo(
    () =>
      selectedDrone
        ? aircraftYawAvailability({
            featureEnabled: features.aircraftYawKeys,
            missionRunning: liveMissionDroneIds.has(selectedDrone.drone_id),
            drone: selectedDrone,
            manufacturer: selectedDroneRow?.manufacturer,
            isCompanion: selectedCommandRoute === 'dji_companion',
            registryKnown: selectedDroneRow != null,
            commandSecretPresent: selectedCommandSecretPresent,
            canOperate: canDispatch,
            companionOnline: selectedDroneCompanion?.is_online ?? null,
            heldBy: manualHoldRefusal({
              hold: manualHold,
              ownSessionIds: ownManualSessions,
              selfOperator: user?.email ?? null,
            }),
          })
        : { offered: false, available: false, reason: null },
    [
      features.aircraftYawKeys,
      liveMissionDroneIds,
      selectedDrone,
      selectedDroneRow,
      selectedCommandRoute,
      selectedCommandSecretPresent,
      selectedDroneCompanion,
      canDispatch,
      manualHold,
      ownManualSessions,
      user?.email,
    ],
  );

  // `manualRef`, the stage layout and the split preference are declared above,
  // beside the control-surface measurement, which needs the layout to know
  // which box a session would fly from.
  // Paint only. No `onBeforeChange` partner to the layout hook's: hiding the
  // graduations changes nothing the vector is normalised to, so there is no
  // drag to cancel and nothing to put on the control plane.
  const {
    visibility: manualRingVisibility,
    setVisibility: setManualRingVisibility,
    persisted: manualRingsPersisted,
  } = useManualRingVisibility({ operator: user?.email ?? null });

  const manual = useManualControl({
    droneId: activeSelectedId,
    /**
     * THE UNION OF BOTH LADDERS, and this is what makes the yaw keys work at all (D19/D20).
     *
     * `request()` returns false when this prop is false, so whatever it is gates every control
     * that can START a session - not only the MANUAL key it was written for. Passing the manual
     * ladder alone meant the camera's yaw keys rendered pressable on exactly the surface their
     * own ladder was written to serve (a narrow map column) while every press did nothing; and
     * with `MANUAL_FLIGHT_RELEASED` false it meant they could never acquire a session at all.
     *
     * The union does not widen manual flight: the MANUAL key reads `manualAvailability` for
     * itself and is unchanged, and the server and the RC apply their own gates regardless. It
     * widens only which controls may ASK.
     */
    available: manualAvailability.available || aircraftYaw.available,
    unavailableReason: manualAvailability.reason,
    subscribeManualControlState,
    subscribeDroneAck,
    layout: manualLayout,
    // A reload mid-session resumes rather than showing IDLE over an aircraft
    // this console is still holding. Only ever adopted when the lock names THIS
    // account — see the hook's rehydration effect.
    rehydrate: manualSnapshot,
    selfUserId: user?.id ?? null,
  });
  manualRef.current = manual;

  /**
   * Turning the AIRCRAFT to aim the camera sideways (#732).
   *
   * Built here rather than beside `cameraControl` because it needs `manual`, which is declared
   * below that object; the overlay receives it by the same spread the split view already uses
   * for `flightKeysOwnKeyboard`.
   */
  const aircraftAim = useAircraftAim({ manual, availability: aircraftYaw });

  // Remembered so our own broadcast frames are never read back as another
  // operator's hold. Bounded: only the newest session can still be arriving on
  // the wire, so a long shift cannot grow this without limit.
  useEffect(() => {
    const id = manual.sessionId;
    if (!id) return;
    setOwnManualSessions((prev) =>
      prev.has(id)
        ? prev
        : prev.size >= OWN_MANUAL_SESSION_MEMORY
          ? new Set([id])
          : new Set(prev).add(id),
    );
  }, [manual.sessionId]);

  /**
   * The operator is HAND-FLYING - which an aim session is not.
   *
   * The COP had one global notion of "manual flight is happening", driven by the controller's
   * state alone. Since a camera key acquires a session by itself (#732), that made one press of
   * an arrow flip the whole console into hand-flying: the stage re-hosted and unmounted the
   * picture being aimed, a flight banner appeared, the drag ring mounted, and every camera key
   * went dead for the length of the session - so the feature disabled itself after one press,
   * and W/S silently stopped being gimbal tilt and became aircraft translate.
   *
   * `aircraftAim.ownsControl` is what separates the two. An aim session still HOLDS authority -
   * the person beside the aircraft is still locked out, and the picture says so - but it is not
   * a hand-flying session and the COP must not present or arm one. Record: D26, D27.
   */
  const aimOwnsControl = aircraftAim.ownsControl;
  aimOwnsControlRef.current = aimOwnsControl;
  const manualActive = (manual.holdsAuthority || manual.state === 'REQUESTED') && !aimOwnsControl;

  /**
   * Tell the shell the aircraft is in the operator's hand, so the rail stops
   * opening itself over the strip board.
   *
   * The hazard it removes is one click: a hover-expanded rail covers the rows,
   * its nav links sit in the same vertical band as them, and a `<Link>` press
   * unmounts `CopView` - which releases manual authority on the wire in
   * `useManualControl`'s drone-change cleanup. `manualExitGuard` confirms the
   * three SELECTION paths to that outcome and cannot see this one, because a
   * navigation is not a `SelectionAction`. See `manualFlightEngaged`.
   *
   * `manualActive` rather than `holdsAuthority`, so the request window counts:
   * a grant can be up to `grant_timeout_ms` (3 s) behind the press, and the
   * rail must not be allowed to open into that gap and still be sitting there
   * when the aircraft arrives.
   */
  useEffect(() => {
    setManualFlightEngaged(manualActive);
    // Leaving the COP hands the aircraft back anyway, but the flag outlives this
    // component in the layout's provider - so it is cleared here rather than
    // left true over a route with no aircraft on it.
    return () => setManualFlightEngaged(false);
  }, [manualActive, setManualFlightEngaged]);

  /**
   * The picture is still worth watching, even though the control is gone.
   *
   * `OVERRIDE` and `FAULT` are not holding states, so everything keyed on
   * `holdsAuthority` used to vanish in the same frame the RC pilot grabbed the
   * sticks: the stage snapped from VIDEO or SPLIT back to a full map and the HLS
   * player was destroyed. An operator watching an aircraft thread a gap lost the
   * picture at the exact moment they could no longer influence what it showed —
   * and the aircraft is still airborne and still needs watching.
   *
   * So the CONTROL surface follows `holdsAuthority` and the PICTURE follows this.
   * Deliberately NOT folded into `holdsAuthority`, which gates real
   * command-sending in a dozen places and must keep meaning exactly one thing.
   * Deliberately narrower than `manualBannerShown` too: `RELEASING` is the
   * operator asking to be done, and returning them to the map is the answer.
   */
  /**
   * An AIM session is excluded (D26). This is the flag that RE-HOSTS the picture into the manual
   * stage, and re-hosting it is exactly what must not happen for a camera key: the operator is
   * already looking at that picture, in the place they put it, and a press to look slightly left
   * moved it into the corner PiP and then moved it back eight seconds later.
   *
   * `OVERRIDE` and `FAULT` still count, aim session or not - those mean control was taken away,
   * which is the case this flag was written for.
   */
  const manualPictureHeld =
    (manual.holdsAuthority && !aimOwnsControl)
    || manual.state === 'OVERRIDE' || manual.state === 'FAULT';
  // The banner is a session strip: it belongs to a session that exists. Every
  // other thing manual flight has to say — and a refusal is the commonest — is
  // said by the note below it, which outlives the toast that used to carry it.
  const manualBannerShown =
    manual.state !== 'IDLE' && manual.state !== 'UNAVAILABLE' && !aimOwnsControl;
  /**
   * The stage outside a session: the selected aircraft's picture beside the map,
   * half each, drawn by the same component manual flight uses, behind the
   * deployment switch. A session that EXISTS - from REQUESTED to RELEASING, the
   * span the banner is on screen for - owns the stage through the banner's own
   * layout switch, so the split yields to `manualBannerShown` and not merely to
   * a held session; otherwise two stage controls are drawn at once and can
   * disagree (D8). The preference is neither read nor written by the session
   * (D3). Keyed on the resolved aircraft, not the id, so the picture is never
   * asked for an aircraft the roster cannot show.
   */
  const splitVideoActive = splitStageRequested && !manualBannerShown;
  const regularStageLayout = splitVideoActive ? 'split' : 'map';
  /** The VIDEO key: drawn whenever the split could be asked for, selected aircraft or not. */
  const splitVideoOffered =
    features.copSplitVideo && !manualBannerShown && manualDevice.deviceClass !== 'phone';
  /** Where the stage is drawing the selected aircraft's picture, if it is. */
  const stagePlacement = manualPictureHeld ? manualLayout : splitVideoActive ? 'split' : null;

  /**
   * A stage appearing takes its camera back from the window nobody asked for.
   *
   * The map-click gate is one-directional on its own: it refuses to open a
   * follow window while a stage is already drawing, but nothing retired that
   * window when a stage appeared AFTERWARDS — which is the ordinary order of
   * events. Click the aircraft on the map (the only way to select it there), a
   * small follow window opens; then press MANUAL. The window's camera is in
   * `selectedPoppedCameras`, `chooseStream` excludes every camera in a window,
   * and the manual stage an operator is about to fly a tray approach on renders
   * "Playing in its own window" with a recovery key instead of the picture. On
   * a two-camera airframe it is quieter and worse: the stage silently draws the
   * DOWN camera because the nose is in the window.
   *
   * Only the transient one. A pinned window is the operator's own decision and
   * outranks the stage; they can dock it back themselves, and the window offers
   * exactly that verb once the stage is up (`stageTakesBack`).
   */
  useEffect(() => {
    if (stagePlacement === null || activeSelectedId === null) return;
    const transient = selection.cameraWindows.find(
      (w) => w.kind === 'drone' && !w.pinned && w.droneId === activeSelectedId,
    );
    if (transient) closeCameraWindow(transient);
  }, [stagePlacement, activeSelectedId, selection.cameraWindows, closeCameraWindow]);

  // Which bottom corner the control takes on a tablet, remembered per operator
  // exactly as the stage layout is.
  const {
    handed: manualHanded,
    setHanded: setManualHanded,
    persisted: manualHandedPersisted,
  } = useManualHandedness({ operator: user?.email ?? null });

  // Held only while authority is. A tablet that sleeps mid-vector takes the STOP
  // key with it; a refusal is surfaced in the banner rather than swallowed.
  const wakeLock = useScreenWakeLock(manual.holdsAuthority);

  /**
   * The pending `FLY TO HERE` target — placed by a long press on the control
   * surface, refined by dragging the pin, and NOT SENT until confirmed.
   *
   * Held here rather than inside the surface so the confirm bar can be a strip
   * BELOW the map: a dialog over the map would cover the pin, the obstacles
   * around it and the aircraft it is a leg from — exactly what the operator has
   * to see before agreeing to fly there.
   */
  const [manualFlyTarget, setManualFlyTarget] = useState<ManualFlyTarget | null>(null);
  // A target belongs to a manual session. Losing authority — released, overridden
  // at the RC, or faulted — takes it with it.
  useEffect(() => {
    if (!manual.holdsAuthority) setManualFlyTarget(null);
  }, [manual.holdsAuthority]);
  // #812: taking the sticks back answers a held goto question - the operator chose to fly by hand.
  // Left up, "Fly anyway" would send a goto to an RC taking virtual-stick input, which the command
  // bar itself refuses while manual control is live.
  const { dismissPending: dismissGotoQuestion } = droneCommand;
  useEffect(() => {
    if (manual.holdsAuthority) dismissGotoQuestion();
  }, [manual.holdsAuthority, dismissGotoQuestion]);

  /**
   * The confirm bar is busy from the moment CONFIRM is pressed — through the
   * release, not only through the POST. Held as a ref as well as state because
   * two Enters in the same tick would otherwise both pass the disabled check and
   * dispatch two legs.
   */
  const [flyToBusy, setFlyToBusy] = useState(false);
  const flyToInFlightRef = useRef(false);

  /**
   * What became of a dispatched leg once the sticks were already handed back.
   *
   * Only ever set for an outcome the operator has to act on — the RC holding the
   * GO-TO for an ARM, or nothing confirming it at all. A leg the aircraft is
   * flying says nothing here; the command bar's own ack readout has it.
   *
   * The hook owns the whole lifetime, because the verdict arrives up to six
   * seconds after the press and both ends of that gap matter: it is dropped if
   * the console moved on in the meantime, and it is retracted if the aircraft
   * confirms the leg late. See `useManualFlyToOutcome`.
   */
  const flyTo = useManualFlyToOutcome({
    selectedDroneId: activeSelectedId,
    holdsAuthority: manual.holdsAuthority,
    subscribeDroneAck,
    onLateConfirmed: () => toast.success(MANUAL_FLY_TO_LATE_CONFIRMED),
  });

  /**
   * Dispatch a CONFIRMED target through the EXISTING `goto` verb and its
   * existing ack. No new backend path: this is the same command the GO-TO map
   * click has always sent, with the altitude answered by the altitude rail
   * instead of by the GO-TO strip.
   *
   * Reached from exactly one place — the confirm bar. The surface itself never
   * dispatches: a 500 ms press proposes a target, and the operator agrees to it.
   *
   * Manual authority is handed back FIRST and the release is awaited — the RC
   * refuses a `goto` while it holds a manual session, so this is the only order
   * in which the command exists at all. Then the leg's own ack is watched,
   * because that release cannot be taken back and the RC holds a `goto` on an
   * unarmed aircraft: "command sent" over an aircraft that is hovering with the
   * sticks gone is the one thing this flow must never report. See
   * `dispatchManualFlyTo`.
   */
  const sendManualFlyTo = useCallback(
    async (target: ManualFlyTarget) => {
      if (flyToInFlightRef.current) return;
      flyToInFlightRef.current = true;
      setFlyToBusy(true);
      // Stamps the aircraft, the authority state and a generation NOW, so the
      // verdict that lands seconds from here can tell whether it is still about
      // the screen in front of the operator.
      const leg = flyTo.begin();
      // The strip's work is done the moment the leg is on the wire; the ack is
      // watched for seconds afterwards and must not hold a "not sent yet" bar
      // over the map while it is.
      const closeStrip = () => {
        flyToInFlightRef.current = false;
        setFlyToBusy(false);
        // The session that owned this target is over, and a pin left on the map
        // would belong to authority nobody holds. (The holdsAuthority effect
        // above does this too — this is the path that does not depend on the
        // release having succeeded.)
        setManualFlyTarget(null);
      };
      try {
        const outcome = await dispatchManualFlyTo({
          target,
          release: () => manual.release('operator'),
          send: droneCommand.send,
          wasQuestioned: droneCommand.lastSendQuestioned,
          watchAck: (commandId) => watchFlyToAck({ commandId, subscribe: subscribeDroneAck }),
          onDispatched: closeStrip,
          onReleaseFailed: (message) => toast.error(message),
          onNotFlying: (message, outcome, commandId) => {
            // Said where it persists first: a toast is four seconds and this is
            // an aircraft that may be holding still with nobody flying it.
            const report = leg.report({ message, outcome, commandId });
            // And said once out loud — unless the operator has already answered
            // this leg by taking the sticks back or confirming another one, in
            // which case the sentence contradicts what they just did.
            if (report === 'recorded' || report === 'stale-selection') toast.warning(message);
          },
        });
        // #812: the leg was held back as a question. The sticks are already handed back and
        // NOTHING is flying or uploading - which is the one thing the question strip does not say.
        if (outcome === 'question') {
          toast.warning('Fly here not sent yet - manual control is already handed back.'
            + ' KEEP ON STATION leaves it hovering; take manual control to fly it by hand.', { duration: 10_000 });
        }
      } catch (err) {
        // Nothing in `dispatchManualFlyTo` is EXPECTED to throw — `release`
        // resolves false rather than rejecting and `droneCommand.send` answers
        // null and toasts its own failure — so anything arriving here is an
        // unforeseen one, and without this it would be an unhandled rejection
        // with the operator told nothing at all. By this point the sticks are
        // already gone (the release is the first thing that flow does), so the
        // one thing that must be said is that the leg is not on its way.
        console.error('FLY TO HERE dispatch failed', err);
        toast.error('Fly here was not sent. The command failed on the way out. Take manual control again to fly the drone.');
      } finally {
        closeStrip();
      }
    },
    [droneCommand.send, droneCommand.lastSendQuestioned, flyTo, manual, subscribeDroneAck],
  );

  // The camera lock. Releasable while there is a map to explore; in the VIDEO
  // layout the minimap is a control and the lock is not the operator's to give
  // up (see useManualMapLock).
  const mapLock = useManualMapLock({
    // Not for an aim session (D26). The lock exists because manual flight draws its grab halo at
    // the CENTRE of the map, so the aircraft has to be held there - and an aim session mounts no
    // halo at all. Without this, looking slightly left snapped the operator's map onto the
    // aircraft and held it for the length of the session.
    enabled: manual.holdsAuthority && !aimOwnsControl,
    releasable: manualLayout !== 'video',
  });

  // Map-pick mode and manual mode cannot both own the map click. Taking
  // authority disarms the pick rather than leaving two armed modes and a click
  // that could plausibly mean either.
  //
  // A blanket DISARM, not just the fly-here pick this guarded when 'goto' was
  // the only armed mode there was. #279 turned that boolean into seven map
  // modes, and every one of them claims the same click manual flight now needs
  // — so while an operator holds authority, none of them may hold the map.
  // Nothing is destroyed by it: standing a plan mode down ends POINT ENTRY and
  // keeps the drawn route (see `standDownModeRef` and the compose overlay's
  // onModeExit), so a half-drawn mission survives a manual session.
  useEffect(() => {
    if (manualActive) disarmMapMode();
  }, [manualActive, disarmMapMode]);

  /**
   * The map shrinks to the 280 px minimap, so the ruler puts itself down.
   *
   * `DroneMap` refuses to DRAW a measurement while compact - the control column
   * is suppressed at that size, and the column holds both the ruler key and
   * CLEAR, so ink there would be ink with nothing that can remove it. But
   * refusing to draw is only half of it: without this the mode stayed armed and
   * the minimap went on claiming every click for a measurement nobody could
   * see, silently adding points to a line the operator had built on the full
   * map.
   *
   * Reachable because the manual-authority disarm above fires on the TRANSITION
   * into a session; an operator in the map layout can arm the ruler afterwards
   * and then switch the video onto the stage. The points are untouched (D10),
   * so the measurement is still there when the map comes back.
   */
  const mapIsCompact = manualPictureHeld && manualLayout === 'video';
  useEffect(() => {
    if (!mapIsCompact) return;
    dispatchMapMode({ type: 'DISARM_KIND', kind: 'range-bearing' });
    // The minimap has no LOOK key to cancel with and draws no ring, so a LOOK
    // left armed there would send a real tilt from a click nobody could see
    // was spoken for (slice 3 review).
    dispatchMapMode({ type: 'DISARM_KIND', kind: 'look-here' });
  }, [mapIsCompact]);

  const handleToggleManual = useCallback(() => {
    // Anything that is not plain IDLE/UNAVAILABLE is handed back rather than
    // re-requested: from OVERRIDE and FAULT the release is what clears the state.
    if (manual.state !== 'IDLE' && manual.state !== 'UNAVAILABLE') {
      // Nothing here waits on the outcome — the key is a toggle, and a release
      // that failed says so in the banner rather than by blocking a button.
      void manual.release('operator');
      return;
    }
    // Same reasoning as the effect above, on the other entry into a session:
    // the map belongs to manual flight from the moment authority is requested.
    disarmMapMode();
    manual.request();
  }, [manual, disarmMapMode]);

  /**
   * The keep-in boundary the manual projection is clamped against: the active
   * operational fence the aircraft is actually inside. Fences it is outside are
   * irrelevant to a 20 m vector, and picking "the first one" would clamp against
   * a boundary somewhere else on the map.
   */
  const manualKeepIn = useMemo<GeoJSON.Geometry | null>(() => {
    if (!selectedDrone) return null;
    for (const fence of keepInFences) {
      const ring = extractPolygonRing(fence.geometry);
      if (ring && pointInPolygonRing(selectedDrone.latitude, selectedDrone.longitude, ring)) {
        return fence.geometry;
      }
    }
    return null;
  }, [keepInFences, selectedDrone]);

  // The predictions request is org-wide and already made for the header KPI, so
  // the aircraft's own rows are a filter rather than a second call.
  const selectedDroneMaintenance = useMemo(() => {
    if (!activeSelectedId) return [];
    return maintenancePredictions.filter((p) => p.drone_id === activeSelectedId);
  }, [maintenancePredictions, activeSelectedId]);

  /**
   * Which sheet holds the right column. One column, three claimants; the
   * missions panel wins by precedence (its OPEN leaves drone/dock selection
   * intact underneath, so the other tabs lead straight back), then the dock
   * sheet, then the drone workspace — the same order the reducer's transitions
   * assume.
   */
  /**
   * Whether the layout is at Tailwind's `lg` (64rem) or wider — i.e. whether the
   * sheets in the right column are side panels or bottom drawers.
   *
   * Read through `matchMedia` rather than a resize handler because the only
   * consumer is a boolean at one breakpoint, and false on the server is the
   * right first paint: the drawer form is the mobile-first one. Same shape as
   * the `(pointer: coarse)` subscription in `useManualDevice`, including the
   * guard — jsdom defines no `matchMedia`.
   */
  const [lgUp, setLgUp] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const q = window.matchMedia('(min-width: 64rem)');
    const sync = () => setLgUp(q.matches);
    sync();
    q.addEventListener?.('change', sync);
    return () => q.removeEventListener?.('change', sync);
  }, []);

  /**
   * The bottom bars float over the map rather than sitting under it.
   *
   * `lg` and up only. Below it the details panel is a `fixed z-40` drawer and
   * these bars beat it with `z-50` deliberately - see the comment above them, and
   * the defect it records: a GO-TO altitude bar behind the drawer sent a leg out
   * at an altitude the operator never saw. Question D of #505 (which display, and
   * touch or mouse) has not been answered, so the layout below `lg` is left
   * exactly as it is rather than designed for a device nobody has named.
   */
  const bottomStackFloats = lgUp;
  // Same rule and the same reason: at `lg` the rows are drawn over the map and
  // publish what they cover, below it they are ordinary flex children and cover
  // nothing.
  const topStackFloats = lgUp;

  /**
   * The zones tab is showing.
   *
   * First in the precedence chain below, because it is the only one of the four
   * that is opened by an explicit press and by nothing else - a dock or a drone
   * selection opens its own sheet as a side effect, and letting either of those
   * take the column out from under a half-drawn polygon would throw the geometry
   * away.
   *
   * Gated on `lgUp` and on the role. Below `lg` the tab strip is `hidden`, so
   * there would be no way back off this sheet; and the panel has never rendered
   * below `lg` anyway, which is exactly why the map key that used to toggle it
   * did nothing there.
   */
  const zonesTabOpen = zone.showPanel && lgUp && canDispatch;

  const activeSheet: CopSheetTab | null = zonesTabOpen
    ? 'zones'
    : selection.missionsPanelOpen
      ? 'missions'
      : selection.selectedDockId
        ? 'dock'
        : selection.detailsOpen && selectedDrone
          ? 'drone'
          : null;

  /**
   * The drone workspace is actually VISIBLE — open, holding the column, and not
   * peeked shut. Three separate pieces of state have to agree, and every control
   * that toggles the panel has to ask this rather than `detailsOpen`, or it ends
   * up closing something nobody can see.
   *
   * `lgUp` is part of the question because the collapse is a DRAWER idea: the
   * grab handle is `lg:hidden` and the body's collapse class is
   * `collapsed && 'hidden lg:flex'`, so at `lg` a collapsed panel is fully on
   * screen. Without this term, peeking the drawer shut on an iPad in portrait
   * and then rotating to landscape left ⊞ DETAILS unlit over a visible panel,
   * with the handle now `display: none` and no way at all to clear the flag —
   * the same dead-control bug the reducer lift was meant to end, relocated to a
   * breakpoint crossing.
   */
  const detailsOnScreen = activeSheet === 'drone' && (lgUp || !selection.detailsCollapsed);

  const sheetDroneLabel = selectedDrone
    ? selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
    : null;
  const sheetDockLabel = selection.selectedDockId
    ? selectedDockStatus?.callsign ?? selectedRegistryDock?.callsign ?? selectedRegistryDock?.name ?? 'DOCK'
    : null;

  const handleSelectSheetTab = (tab: CopSheetTab) => {
    if (tab === 'zones') {
      // Pressing ZONES while it is showing closes it the SAME way every other
      // exit does - CLOSE_PANEL, which carries DRAW_RESET, plus a stand-down.
      // TOGGLE_PANEL alone would keep the collected points while removing the
      // panel and disarming the map, which leaves a half-drawn polygon painted
      // over the picture with nothing on screen that can finish, name or clear
      // it.
      if (zone.showPanel) leaveZones();
      else dispatchZone({ type: 'TOGGLE_PANEL' });
      return;
    }
    // Moving to any other sheet leaves zones, and leaving zones stands the
    // drawing down: this panel is the only place a collected geometry can be
    // named and saved, so a shape left armed behind another sheet would keep
    // taking the map clicks the operator now means for that sheet.
    if (zone.showPanel) leaveZones();
    if (tab === 'missions') {
      dispatchSelection({ type: 'OPEN_MISSIONS_PANEL' });
    } else if (tab === 'drone') {
      dispatchSelection({ type: 'OPEN_DETAILS' });
    } else if (selection.selectedDockId) {
      // Re-selecting the same dock keeps its bay/launch state (sameDock path)
      // while clearing the missions panel's precedence.
      dispatchSelection({ type: 'SELECT_DOCK', id: selection.selectedDockId });
    }
  };

  const siteFilterLabel = siteId ? copSites[0]?.name ?? siteId : null;
  // Starts MINIMISED, so a fresh COP gives the band back to the map: a dock
  // icon and an arrow, no chips. Deliberately not persisted - "by default the
  // docks bar should be collapsed" is the ask, and remembering an open bar
  // would restore it on the load where the map mattered most.
  const [dockRosterView, setDockRosterView] = useState<DockRosterView>('minimised');
  const contextDataDegraded =
    missionsCountFailed || maintenanceCountFailed || geofencesFailed || sitesFailed || droneRegistryFailed;
  /**
   * DEGRADED is about the SYSTEM, not the fleet.
   *
   * This used to include the count of unresolved alerts, so ONE of them - any
   * alert, any age, any severity - pinned the header to DEGRADED until somebody
   * cleared it. On 2026-09-08 prod read DEGRADED on fifteen alerts,
   * every one of them stale from the previous afternoon, while `/health`
   * reported `status: healthy` with an empty `warnings` array and all three
   * dependencies connected. A header that says DEGRADED when nothing is wrong
   * is a header an operator stops reading, which costs exactly the alert the
   * badge exists to carry.
   *
   * The fleet already has its own indicator, and it is the right one: the alert
   * bell beside this word. It counts a NARROWER list - `status === 'active'`,
   * i.e. unacknowledged - where this rule counted `!== 'resolved'`. That gap was
   * part of the bug: an alert somebody had already acknowledged had stopped the
   * bell flashing but still held this word at DEGRADED.
   *
   * What remains is what an operator can do nothing about from the map: the
   * live-update socket, and the API failing to serve the context the COP is
   * built on. `TopBar` adds the three dependencies it polls from `/health`
   * (database, cache, MQTT) and the socket state to the same verdict, so the
   * word and the dots beneath it cannot disagree.
   */
  const systemStatus = copSystemStatus({
    liveUpdates: connected,
    contextDataFailed: contextDataDegraded,
    coverageUnread: missionsQueue.coverageUnread.unread > 0,
  });

  return (
    <StageInsetsProvider controller={stageInsetsController}>
    {/* Video analytics on/off per feed, shared by the stage and the video windows. */}
    <FeedAnalyticsProvider>
    {/* A manual session's request and release take the stage away for seconds;
        the selected aircraft's analytics must still be on when it comes back.
        Only that gap - the banner up, the picture not held. Across the whole
        session it would keep a camera closed with X armed until the stage
        showed it again (D6). */}
    <FeedAnalyticsClaim
      droneId={manualBannerShown && !manualPictureHeld ? selectedDrone?.drone_id ?? null : null}
    />
    <div className="flex flex-col h-full">
      <TopBar
        title={AIR_PICTURE_TITLE}
        subtitle={organization?.name}
        // At `lg` and up this drops the subtitle and the title's extra height:
        // the org name does not change while an operator watches it, and the map
        // wants the 12 px. Below `lg` the header is untouched.
        dense
        // Slice 7: the site filter says so on the control that sets it, rather
        // than in a banner of its own in the map's flex column - a row that
        // appeared with the filter and resized the MapLibre canvas doing it.
        siteFilterLabel={siteFilterLabel}
        // The rail floats over the full height of the COP, this header
        // included, so the nav button, the title and the site picker stand
        // clear of it rather than being drawn underneath.
        // Not gated on `lgUp`. `railInsetPx` is already 0 whenever the rail is
        // not floating, and `lgUp` is a 64rem media query while the rail's own
        // threshold is 1024 CSS px - at a 20 px root font those disagree between
        // 1024 and 1280, and the header alone would draw under the rail.
        leftInsetPx={railInsetPx}
        status={systemStatus}
        wsConnected={connected}
        alerts={
          <CopAlertBell
            alerts={alerts}
            feedState={alertFeedState}
            updatedAt={alertsUpdatedAt}
            // Gated exactly as the drone card's copy of these two verbs is
            // (`:2532`). A viewer used to see both keys in the alert list and
            // get silence from `useCopAlerts`'s `canAckResolve` guard.
            onAcknowledge={canAckResolve ? handleAcknowledge : undefined}
            onResolve={canAckResolve ? handleResolve : undefined}
            onFlyToDrone={handleFlyToDrone}
          />
        }
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              // Not `missionsPanelOpen` alone: the zones sheet outranks the
              // missions queue for the column, so while zones is showing this
              // key would light up over a panel the operator cannot see.
              aria-pressed={activeSheet === 'missions'}
              onClick={() =>
                openSheetFromOutside(() =>
                  dispatchSelection(
                    activeSheet === 'missions'
                      ? { type: 'CLOSE_MISSIONS_PANEL' }
                      : { type: 'OPEN_MISSIONS_PANEL' },
                  ),
                )
              }
              className={`flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-[10px] uppercase tracking-wider transition-colors ${
                activeSheet === 'missions'
                  ? 'border-primary/50 bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
              }`}
            >
              Missions
              {queueLiveCount > 0 && (
                <span className="rounded bg-status-green/15 border border-status-green/40 px-1 text-status-green-fg tabular-nums">
                  {queueLiveCount}
                </span>
              )}
            </button>
            <CopDroneSearch drones={copDrones} alerts={alerts} onSelect={handleFlyToDrone} />
          </div>
        }
      />

      {/* The alert surface is NOT in this column any more. It used to be an
          in-flow row here: `null` at zero alerts, 40 px at one, up to 280 px
          expanded — and `DroneMap`'s ResizeObserver turned each of those into a
          real MapLibre canvas resize, so an alert arriving moved the drone the
          operator was tracking. It then floated over the map as `CopAlarmHud`,
          and is now the bell in the header above (`CopAlertBell`), which is a
          sibling of this column and fixed in height. */}
      {/* Full bleed at `lg` and up: the 16 px gutters on four sides plus the row
          gaps are, on a 1366 x 768 laptop, most of a command bar's worth of map.
          Below `lg` the padded layout stays, along with everything else. */}
      <div className="flex-1 flex flex-col overflow-hidden p-4 gap-3 lg:p-0 lg:gap-0">
        {/* LEFT (DOCKS roster + map, stacked) and, at `lg`, a RIGHT column that
            no longer takes any of it: `lg:relative` is what CopSidePanel is
            positioned against, and this box's right edge is the map's. Below
            `lg` the right column is `contents` and each sheet its own fixed
            drawer, so this is two flex children exactly as it was. */}
        <div
          className="flex-1 flex min-h-0 min-w-0 gap-3 lg:relative lg:gap-0"
          style={{ '--dock-sheet-width': `${dockSheetWidth}px` } as CSSProperties}
        >
          {/* ONE column now, where there were two. The outer one existed only to
              stack the dock roster above the map; slice 7 floats the roster, so
              it had a single child left and is gone. `lg:relative` is what both
              floating stacks are positioned against, so this box's edges have to
              be the map's.

              Nothing conditional is an in-flow child of this box any more, which
              is what `tactical-shell-invariants.test.ts` pins: every surface that
              comes and goes over the COP is a floating host, and none of them
              can resize the MapLibre canvas underneath. */}
          <div className="flex-1 flex flex-col min-h-0 min-w-0 gap-2 lg:relative lg:gap-0">
            <CopTopStack floating={topStackFloats}>
              {dockRoster.length > 0 && (
                // `pointer-events-auto`, opting back in from the host's inert
                // box; `pb-1 lg:pb-0` restores the 12 px this row had below `lg`
                // from the outer column's `gap-3`, which the merge above took
                // down to the inner column's `gap-2`.
                //
                // `lg:w-fit lg:max-w-full` - the bar is as wide as the docks it
                // holds, not as wide as the screen. Two docks used to draw a
                // strip across the whole COP with a metre of empty chrome in
                // it, which reads as a panel that failed to load rather than a
                // list with two things in it.
                //
                // It is not only cosmetic. This wrapper is `pointer-events-auto`,
                // so every pixel of that empty strip was swallowing map clicks -
                // and the COP dispatches a GO-TO on the next map click with no
                // dialog, so a swallowed click is a command the operator issued
                // and never sees land. That is the same hazard the host's own
                // comment gives for keeping the 8 px inter-row gap inert.
                //
                // `max-w-full` keeps the cap: with enough docks the bar still
                // stops at the column and the roster's own scroller and chevrons
                // take over, which is what they are for.
                //
                // Minimised the bar is a single key, so `w-fit` applies at EVERY
                // width rather than only at `lg`. Below `lg` that is cosmetic -
                // the bar is in the map's flow there, so a full-width box
                // swallows no map clicks - but a lone icon stretched across the
                // column reads as a panel that failed to load.
                <div
                  className={`shrink-0 pb-1 lg:pb-0 lg:pointer-events-auto lg:max-w-full ${
                    dockRosterView === 'minimised'
                      ? 'w-fit'
                      : dockRosterView === 'wrapped'
                        ? 'lg:w-full'
                        : 'lg:w-fit'
                  }`}
                >
                  <CopDockRoster
                    docks={dockRoster}
                    selectedDockId={selection.selectedDockId}
                    onSelect={handleSelectDockFromRoster}
                    view={dockRosterView}
                    onChangeView={setDockRosterView}
                  />
                </div>
              )}

              {/* Above the stage, not below it: the session strip is where the
                  operator reaches for the layout switch and RELEASE, and field
                  testing put it at the top, clear of the FLY TO HERE confirm
                  bar and the GO-TO strip that own the space below the map. */}
              {selectedDrone && manualBannerShown && (
                <div className="shrink-0 lg:pointer-events-auto">
                  <ManualFlightBanner
                    manual={manual}
                    callsign={
                      selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
                    }
                    layout={manualLayout}
                    onLayoutChange={setManualLayout}
                    layoutPersisted={manualLayoutPersisted}
                    ringVisibility={manualRingVisibility}
                    onRingVisibilityChange={setManualRingVisibility}
                    ringsPersisted={manualRingsPersisted}
                    pointer={manualDevice.pointer}
                    handed={manualHanded}
                    onHandedChange={setManualHanded}
                    handedPersisted={manualHandedPersisted}
                    wakeLockRefused={wakeLock.refused}
                    batteryLevel={manualDevice.batteryLevel}
                  />
                </div>
              )}
            </CopTopStack>

            {/* The stage. Outside a manual session it is the map, or the map
                and the selected aircraft's picture side by side when the
                operator has asked for that (the VIDEO key in the map's control
                column); inside one it is whichever of the
                three layouts the operator chose, with the SAME map node in
                the same place in the tree — see ManualStage on why that
                matters to a MapLibre instance. */}
            <ManualStage
              layout={manualPictureHeld ? manualLayout : regularStageLayout}
              active={manual.holdsAuthority}
              onMapInteraction={mapLock.interact}
              mapBoxRef={mapBoxRef}
              stageBoxRef={stageBoxRef}
              sessionSplitBoxRef={sessionSplitBoxRef}
              pointer={manualDevice.pointer}
              // The divider is the regular-mode split's only: a session's SPLIT
              // is half and half, always (resize record).
              videoShare={!manualPictureHeld && splitVideoActive ? splitVideoShare : undefined}
              onVideoShareChange={setSplitVideoShare}
              video={
                selectedDrone && (manualPictureHeld || splitVideoActive) ? (
                  <ManualVideoStage
                    droneId={selectedDrone.drone_id}
                    callsign={
                      selectedDrone.callsign?.trim()
                      || selectedDrone.drone_id.slice(-6).toUpperCase()
                    }
                    groundSpeedMs={selectedDrone.speed ?? null}
                    // Outside a session there is no manoeuvre to follow: the
                    // badge prints the delay and its grade, and no phase.
                    manualState={manualPictureHeld ? manual.state : null}
                    poppedCameras={selectedPoppedCameras}
                    compact={manualPictureHeld && manualLayout === 'map'}
                    // While manual authority is held the flight keys own the
                    // keyboard and the picture binds none (P1a D14).
                    cameraControl={cameraControl && { ...cameraControl, flightKeysOwnKeyboard: manualActive, aircraftAim }}
                    // Every camera in its own window leaves this box empty;
                    // this is the way back that does not go through FEED. Scoped
                    // to THIS aircraft — another drone's window is not this
                    // stage's to close.
                    onDockBack={() =>
                      dispatchSelection({ type: 'DOCK_VIDEO', droneId: selectedDrone.drone_id })
                    }
                  />
                ) : null
              }
            >
              <CopMapStage
                compact={mapIsCompact}
                drones={copDrones}
                // The org-wide broadcast, straight into the picture. Until
                // now its only consumer was the refusal on the MANUAL key of
                // the SELECTED aircraft, so a second operator scanning the
                // COP saw an ordinary track and could send RTH to an aircraft
                // somebody was flying by hand. Freshness is decided per row
                // and per frame downstream — see `manualHoldMark`.
                manualHolds={manualControlMap}
                selectedDroneId={activeSelectedId}
                onSelectDrone={handleSelectDrone}
                alerts={alerts}
                cooperativeTraffic={cooperativeTraffic}
                geofences={copGeofences}
                sites={copSites}
                missions={mapMissions}
                selectedMissionId={selectedMissionId}
                hoveredMissionId={hoveredMissionId}
                onSelectMission={handleSelectMission}
                handoverLinks={handoverLinks}
                docks={dockMarkers}
                selectedDockId={selection.selectedDockId}
                onSelectDock={handleSelectDock}
                connected={connected}
                mapMode={mapMode}
                rangeBearing={rangeBearing}
                onDispatchTargetPicked={(lat, lng) => {
                  dispatchQuick({ type: 'PICK_DONE', latitude: lat, longitude: lng });
                  // The pick is spent — one click, one target.
                  dispatchMapMode({ type: 'DISARM_KIND', kind: 'dispatch-pick' });
                }}
                onRequestDispatchHere={
                  canDispatch
                    ? (lat, lng) => dispatchQuick({ type: 'REQUEST_HERE', latitude: lat, longitude: lng })
                    : undefined
                }
                triggers={triggerMarkers}
                onDispatchFromTrigger={canDispatch ? handleDispatchFromTrigger : undefined}
                onDismissTrigger={canDispatch ? (id) => {
                  // The hook rolls the point back into the list on failure, so
                  // silence here reads as a UI glitch: the point vanishes and
                  // returns. The server's refusal text is already operator-grade
                  // ('This trigger was already dispatched') - show it.
                  void dismissTrigger(id).catch((e) => {
                    toast.error(e instanceof Error ? e.message : 'Could not dismiss this detection');
                  });
                } : undefined}
                onApproveTrigger={canDispatch ? (id) => {
                  // The launched sortie is PINNED and the boards reloaded, exactly as the manual
                  // dispatch dialog does it. Without this the operator authorised a launch and
                  // then saw nothing at all change - no chip, no mission row - for the tens of
                  // seconds a dock takes to open a bay, with the vanished point the only feedback
                  // and indistinguishable from a Dismiss. Silent success on a launch action is
                  // how an operator ends up authorising it twice.
                  //
                  // RETURNED, not fire-and-forget: the card awaits this and holds itself open,
                  // disabled and saying "Opening bay", for the whole wait. Rethrown on failure
                  // for the same reason - a card that closed on an error would take the standing
                  // offer off the screen with it.
                  // Named BEFORE the await, because the row IS removed once the launch
                  // succeeds and a name read afterwards would be a name read from nothing. The
                  // whole point of propose-and-approve is that a human approved a NAMED
                  // aircraft, and the one artefact left on screen after the click should say
                  // which.
                  const row = triggers.find((t) => t.id === id);
                  const callsign = row?.proposal_considered
                    ?.find((c) => c.droneId === row.proposed_drone_id)?.callsign ?? null;
                  return approveTrigger(id).then(({ missionId, droneId }) => {
                    if (droneId) handleMissionLaunched(droneId, missionId);
                    toast.success(callsign ? `Launched ${callsign}` : 'Launched', {
                      description: 'The aircraft is being commanded to the detection.',
                    });
                  }).catch((e) => {
                    // A TIMEOUT IS INDETERMINATE, AND MUST NOT READ AS A FAILURE. Every other
                    // holder of LAUNCH_REQUEST_TIMEOUT_MS imports this pair with it; this door
                    // took the constant alone, so an abort surfaced the browser's own "signal
                    // timed out" on a request that has very likely just put an aircraft in the
                    // air. `launch-request.ts` is explicit: the copy sends the operator to
                    // verify, never to blind-retry.
                    toast.error(isLaunchRequestTimeout(e)
                      ? LAUNCH_TIMEOUT_MESSAGE
                      : (e instanceof Error ? e.message : 'Could not launch this aircraft'));
                    throw e;
                  });
                } : undefined}
                onRejectTrigger={canDispatch ? (id) => {
                  void rejectTrigger(id).catch((e) => {
                    toast.error(e instanceof Error ? e.message : 'Could not reject this proposal');
                  });
                } : undefined}
                onGotoPointPicked={(lat, lng) => {
                  // The altitude was answered while pick mode was armed (see
                  // GotoAltitudeBar), so the click still dispatches on its own
                  // rather than opening a dialog over the map the operator is
                  // still working on.
                  void droneCommand.send('goto', {
                    latitude: lat,
                    longitude: lng,
                    altitude_m: gotoAltitudeM,
                  });
                  disarmMapMode();
                }}
                // LOOK HERE (video/gimbal P2): the key after VIDEO, and where
                // the map's click goes while it is armed. Null, so no key, on
                // an aircraft it could never aim.
                lookHere={lookHereOffered ? lookHere : null}
                // The VIDEO key beside FOLLOW and TRACK. Null, so no key at
                // all, when the deployment has not enabled the split and
                // during a manual session, where the banner's layout switch
                // owns the stage.
                videoBeside={
                  splitVideoOffered
                    ? { on: splitVideoOn, onToggle: () => setSplitVideoOn(!splitVideoOn) }
                    : null
                }
                // Video lives in a window over the map, so the map's own verb
                // opens one — it no longer has a panel to send the operator to.
                // PINNED, because this is the operator naming a picture by hand
                // exactly as FEED's key is: the aircraft they click next must
                // not take it away. It cannot know which camera it means, so it
                // asks for the default one; FEED lists them all for a choice.
                onOpenVideo={(droneId) => {
                  dispatchGuardedSelection({ type: 'SELECT', id: droneId }, () =>
                    openCameraWindow({
                      kind: 'drone',
                      droneId,
                      cameraId: SINGLE_CAMERA,
                      callsign: copDroneTracks.get(droneId)?.callsign?.trim() || null,
                      pinned: true,
                    }),
                  );
                }}
                // Identity and the two numbers that decide whether the sortie
                // continues. Everything else the card used to carry here now
                // lives in the details sheet, one click away and off the
                // airspace the aircraft is flying into: mission progress,
                // link, satellites and speed in its fact grid, companion
                // liveness in the row beneath it. All of those sit above the
                // sheet's scrolling area, so opening it is the only cost —
                // telling "the aircraft is off" from "our app died on the RC"
                // still takes one click and no scrolling.
                droneCard={
                  selectedDrone ? (
                    <DroneSelectionChip
                      drone={selectedDrone}
                      alerts={alerts}
                      companion={selectedDroneCompanion}
                      onOpenDetails={() =>
                        openSheetFromOutside(() => dispatchSelection({ type: 'OPEN_DETAILS' }))
                      }
                      // Guarded, not plain. OPEN_DETAILS above leaves the
                      // selection alone so it needs no guard, but this clears
                      // it — and `leavesFlownAircraft` counts that as leaving
                      // the aircraft. While manual authority is held, an
                      // unguarded dispatch here would abandon the session and
                      // hand a flying aircraft back to the RC on one click of
                      // a 24px ✕, with no confirmation. DroneStateCard, which
                      // this chip replaced, was wired to the guard for exactly
                      // this reason; the chip branched before the guard landed
                      // and must not lose it in the merge.
                      onClose={() => dispatchGuardedSelection({ type: 'SELECT', id: '' })}
                    />
                  ) : undefined
                }
                zoneDraw={zoneDraw}
                // The zones sheet's only entry point when no other sheet is
                // open: the tab strip lives inside the right column, which is
                // not rendered at all while `activeSheet` is null.
                onOpenZones={
                  canDispatch
                    ? () => dispatchZone({ type: 'TOGGLE_PANEL' })
                    : undefined
                }
                planWaypoints={planOverlay.waypoints}
                planOrbitRadiusM={planOverlay.orbitRadiusM}
                planHome={planOverlay.home}
                planModeLabel={planModeLabel}
                planEditable={planOverlay.editable}
                onPlanModeExit={planOverlay.onModeExit}
                planSelectedIndex={planOverlay.selectedIndex}
                planActiveIndex={planOverlay.activeIndex}
                onPlanMapClick={planOverlay.onMapClick}
                onPlanWaypointClick={planOverlay.onWaypointClick}
                onPlanWaypointDrag={planOverlay.onWaypointDrag}
                // The control surface only exists while authority is actually
                // held BY THE OPERATOR. Before that there is nothing to fly and a
                // ring drawn over the aircraft would be a control that does nothing -
                // and an aim session holds authority without anyone having asked to
                // hand-fly, so mounting the full rig for one would put a complete
                // manual-flight surface in front of an operator on a deployment where
                // `MANUAL_FLIGHT_RELEASED` is false, without that gate being consulted
                // at all. Record: D27.
                manualOverlay={
                  selectedDrone && manual.holdsAuthority && !aimOwnsControl ? (
                    <ManualMapControl
                      drone={selectedDrone}
                      manual={manual}
                      keepIn={manualKeepIn}
                      altitudeProfile={altitude.profile}
                      layout={manualLayout}
                      focusLocked={mapLock.locked}
                      onRecenter={mapLock.recenter}
                      pointer={manualDevice.pointer}
                      handed={manualHanded}
                      portrait={manualDevice.portrait}
                      flyTarget={manualFlyTarget}
                      // A proposal, never a dispatch: every FLY TO HERE goes
                      // through the confirm bar below the map, at every scale.
                      onFlyTarget={setManualFlyTarget}
                      // The minimap is a control, not a map to explore: it
                      // swallows every press that is not one of its own three
                      // controls, so the camera cannot be panned off the
                      // aircraft the operator is flying.
                      lockPointer={manualLayout === 'video'}
                      ringVisibility={manualRingVisibility}
                    />
                  ) : undefined
                }
                launchOverlay={
                  selection.selectedDockId && selection.launchIntent ? (
                    <DockLaunchOverlay
                      dockId={selection.selectedDockId}
                      isOnline={selectedDockStatus?.is_online ?? selectedRegistryDock?.is_online ?? false}
                      assignedDrone={assignedDrone}
                      plan={missionPlan}
                      dockPosition={
                        selectedRegistryDock?.latitude != null && selectedRegistryDock?.longitude != null
                          ? { lat: selectedRegistryDock.latitude, lng: selectedRegistryDock.longitude }
                          : null
                      }
                      targetDroneId={launchTargetDroneId}
                      launchMode={selection.launchIntent.mode}
                      altitudeProfile={altitude.profile}
                      ceilingAuthority={altitude.authority}
                      altitudeProfileStatus={altitude.status}
                      onSaveDefaultAltitude={role === 'admin' ? altitude.saveDefault : null}
                      onLaunched={handleMissionLaunched}
                      onLaunchFailed={loadMissions}
                      onClose={handleCancelLaunch}
                    />
                  ) : undefined
                }
              />
            </ManualStage>

            {/* Mounted always, empty until there is something to say. A live
                region has to be in the DOM before its content changes or the
                first change is the one nobody hears — and the first change
                here is REQUESTING, which arrives at the same instant the
                banner does. VideoLatencyBadge does this correctly; this is
                the same fix. */}
            {/* A click that would hand the aircraft back, held as a proposal.
                Rendered here rather than inside the stage so it survives any
                re-shape of the stage the pending action might cause. */}
            <ManualExitConfirm
              open={pendingSelection !== null}
              callsign={
                selectedDrone
                  ? selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
                  : ''
              }
              onStay={() => setPendingSelection(null)}
              // The mouse path to the emergency stop, which this dialog's own
              // modal overlay takes away for as long as it is up. Idempotent,
              // and it does not answer the question — see ManualExitConfirm.
              onStop={() => manual.hover()}
              onLeave={() => {
                const pending = pendingSelection;
                setPendingSelection(null);
                if (!pending) return;
                // The session teardown is the existing droneId-change path in
                // `useManualControl` — this only stops holding the click back.
                dispatchSelection(pending.action);
                pending.after?.();
              }}
            />

            <ManualStatusAnnouncer
              manual={manual}
              callsign={
                selectedDrone
                  ? selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
                  : null
              }
            />

            {/*
              Every bar below the map carries `relative z-50 lg:z-auto`.

              Below `lg` the details panel is a drawer — `fixed`, `z-40` — and
              these bars are unpositioned children of the stage column, so
              without a z-index they render UNDERNEATH it. The command bar
              itself is clear either way (the drawer stops at `bottom-[76px]`,
              which is exactly its box), but these four sit ABOVE the command
              bar, which is squarely inside the drawer's band.

              That is not cosmetic. GotoAltitudeBar is where the operator sets
              the altitude a GO-TO will fly at, and `onGotoPointPicked` above
              dispatches the command on the next map tap WITHOUT a dialog,
              specifically because "the altitude was answered while pick mode
              was armed". With the drawer open on a tablet the bar was behind
              it: the altitude was never answered, never seen, and the goto
              went out at whatever the state happened to hold. The FLY TO HERE
              confirmation had the same shape — the operator waits for a strip
              that is rendering behind the panel.

              These bars are transient and they are the thing demanding
              attention, so they win over the panel rather than the reverse.
              On `lg` the drawer is in flow beside the map and cannot overlap
              anything, hence `lg:z-auto`.
            */}

            <CopBottomStack floating={bottomStackFloats}>
            {/* The refusal, once the session that produced it is gone.
                Everything the server or the RC can refuse — held by another
                operator, no command secret, link too slow, a `denied` detail,
                the grant timing out — puts the controller back in IDLE, which
                is exactly the state in which the banner above unmounts. The
                sentence used to survive only as a four-second toast. */}
            {selectedDrone && !manualBannerShown && manual.reason && (
              <div className="pointer-events-auto shrink-0 relative z-50 lg:z-auto">
                <ManualRefusalNote
                  reason={manual.reason}
                  callsign={
                    selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
                  }
                  operator={user?.email ?? null}
                />
              </div>
            )}

            {/* Below the stage, never over it: the operator has to be able to
                see the pin and what is around it while they decide. */}
            {selectedDrone && manual.holdsAuthority && !aimOwnsControl && manualFlyTarget && (
              <div className="pointer-events-auto shrink-0 relative z-50 lg:z-auto">
                <ManualFlyToHereBar
                  callsign={
                    selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
                  }
                  target={manualFlyTarget}
                  busy={flyToBusy || droneCommand.submitting === 'goto'}
                  // Undefined for every DJI companion aircraft — the companion
                  // app publishes no armed field — and the caution says so
                  // rather than guessing. See manualFlyToArmCaution.
                  armed={selectedDrone.armed}
                  onConfirm={() => void sendManualFlyTo(manualFlyTarget)}
                  onCancel={() => setManualFlyTarget(null)}
                />
              </div>
            )}

            {/* The leg went out, the sticks are already gone, and the aircraft
                is not flying it — yet. Persistent, because that is a state to
                act on rather than a notice to read; retracted by the hook if a
                slow wayline upload confirms the leg after all. */}
            {selectedDrone && flyTo.outcome && flyTo.outcome.droneId === activeSelectedId && !manual.holdsAuthority && (
              <div className="pointer-events-auto shrink-0 relative z-50 lg:z-auto">
                <ManualFlyToOutcomeNote
                  callsign={
                    selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()
                  }
                  message={flyTo.outcome.message}
                  held={flyTo.outcome.held}
                  canTakeManual={manualAvailability.available}
                  onTakeManual={() => {
                    flyTo.clear();
                    handleToggleManual();
                  }}
                  onDismiss={flyTo.clear}
                />
              </div>
            )}

            {selectedDrone && gotoArmed && !manualActive && (
              <div className="pointer-events-auto shrink-0 relative z-50 lg:z-auto">
                <GotoAltitudeBar
                  callsign={selectedDrone.callsign?.trim() || selectedDrone.drone_id.slice(-6).toUpperCase()}
                  profile={altitude.profile}
                  authority={altitude.authority}
                  profileStatus={altitude.status}
                  valueMeters={gotoAltitudeM}
                  onChange={setGotoAltitudeM}
                  unit={gotoUnit}
                  onUnitChange={setGotoUnit}
                  onCancel={disarmMapMode}
                />
              </div>
            )}

            {/* A command the server held back as a question, put to the operator once, in the
                goto bar's slot above the command bar, never over the map: #812's goto that would
                cost an approved relief, or (slice 3) with docks off, a TAKEOFF of an aircraft still
                linked to a dock - DISLINK. */}
            {selectedDrone && droneCommand.pendingConfirmation !== null && (
              <div className="pointer-events-auto shrink-0 relative z-50 lg:z-auto">
                <GotoReliefQuestionBar
                  pending={droneCommand.pendingConfirmation}
                  onConfirm={() => { void droneCommand.confirmPending(); }}
                  onCancel={droneCommand.dismissPending}
                />
              </div>
            )}

            {selectedDrone && (
              <div className="pointer-events-auto shrink-0 relative z-50 lg:z-auto">
                <DroneCommandBar
                  drone={selectedDrone}
                  command={droneCommand}
                  // The flight keys take the keyboard only for a session the OPERATOR opened.
                  // An aim session holds authority without anyone having asked to hand-fly (D26).
                  flightKeysArmed={!aimOwnsControl}
                  // One prop, one source of truth: the route the API resolved.
                  // Null while the registry is loading or after it failed, which
                  // suppresses vendor gating rather than guessing at a vendor we
                  // have not actually read.
                  commandRoute={selectedCommandRoute}
                  commandSecretPresent={selectedCommandSecretPresent}
                  commandLinkDown={hasNoCommandLink(selectedDroneCompanion)}
                  commandLinkUnconfirmed={commandLinkUnconfirmed(selectedDroneCompanion)}
                  dockId={selectedDroneRow?.dock_id ?? null}
                  canOperate={canDispatch}
                  onMission={(selectedDroneMission?.waypoints?.length ?? 0) > 0}
                  // The same org profile the dock launch card reads — one
                  // source of truth for presets, ceiling, waiver and unit.
                  altitudeProfile={altitude.profile}
                  ceilingAuthority={altitude.authority}
                  altitudeProfileStatus={altitude.status}
                  gotoArmed={gotoArmed}
                  onToggleGoto={handleToggleGoto}
                  manual={manual}
                  manualDisabledReason={manualAvailability.reason}
                  onToggleManual={handleToggleManual}
                  missionName={selectedDroneMission?.name ?? null}
                  // Lit only when the panel is actually showing. A collapsed
                  // drawer is `detailsOpen` but invisible, and lighting the key
                  // for it made the next press close a panel the operator
                  // could not see — two presses to get it back.
                  // Lit only when the panel is genuinely ON SCREEN, which is
                  // narrower than `detailsOpen` in two ways: a collapsed
                  // drawer is open but invisible, and the missions panel
                  // outranks the drone workspace for the column (see
                  // `activeSheet`). Lighting the key in either case made the
                  // next press dispatch CLOSE_DETAILS at something the
                  // operator could not see, so the panel took two presses to
                  // come back — and below `lg` this key is the only way back,
                  // because CopSheetTabs is `hidden lg:block`.
                  isDetailsOpen={detailsOnScreen}
                  onOpenDetails={() =>
                    openSheetFromOutside(() =>
                      dispatchSelection(
                        detailsOnScreen
                          ? { type: 'CLOSE_DETAILS' }
                          : // Covers "shut", "open but peeked closed", "behind
                            // the missions panel" and "behind the zones sheet"
                            // — OPEN_DETAILS un-collapses and clears
                            // missionsPanelOpen, and the wrapper leaves zones.
                            { type: 'OPEN_DETAILS' },
                      ),
                    )
                  }
                  onOpenDock={handleSelectDock}
                />
              </div>
            )}
            </CopBottomStack>
          </div>

          {/* Every floating camera picture — aircraft cameras popped out of FEED
              and dock cameras opened from the dock sidebar. They are `fixed`, so
              their DOM parent does not matter and they sit alongside the panel.

              They live HERE, at the top of the COP, because this is the only
              component that survives a selection change. Owned any lower — in
              the dock sheet that opened them, or gated on the selected aircraft
              — they died whenever the operator clicked a dock, a drone, or
              another tab, which is the defect this replaced.

              Two things end a window: its own X, and — for the unpinned one the
              map pick opens by itself — the next map pick, which replaces it
              with the aircraft just picked. Nothing else, on any selection
              route, so a picture is never taken away without another arriving.

              Docking is not an ending: it closes the window so the STAGE takes
              the picture back, and the verb is offered only when that is true
              of the window (`stageTakesBack`). */}
          {/* The one announcement of a camera command's failure, whichever and
              however many surfaces are drawing the aircraft's picture (P1a). */}
          <CameraFailureAnnouncer
            gimbalFailure={cameraControl ? droneCommand.failureOf('gimbal') : null}
            zoomFailure={cameraControl ? droneCommand.failureOf('zoom') : null}
            lensFailure={cameraControl ? droneCommand.failureOf('lens') : null}
            gimbalAckId={droneCommand.ackIdOf('gimbal')}
            zoomAckId={droneCommand.ackIdOf('zoom')}
            lensAckId={droneCommand.ackIdOf('lens')}
          />
          <CopCameraWindows
            windows={selection.cameraWindows}
            positions={cameraWindowPositions}
            cascadeSlots={cameraWindowSlots}
            sizes={cameraWindowSizes}
            onSizeChange={resizeCameraWindow}
            onPositionChange={moveCameraWindow}
            onClose={closeCameraWindow}
            onRaise={raiseCameraWindow}
            resolveDronePose={resolveDronePose}
            // Hand the picture back to the STAGE. Closing the window is the
            // whole move now: the stage draws the first camera not in a window,
            // so the moment this one leaves the popped set the stage picks it
            // up. The window only offers this verb when that is true of it
            // (`docksToStage`), so this can never make a picture vanish.
            onDockDrone={closeCameraWindow}
            onTogglePin={toggleCameraWindowPin}
            onPinWindow={pinCameraWindow}
            onDroneStreamState={reportVideoPlayerState}
            resolveDock={resolveDockCameraSource}
            resolveDroneCallsign={(droneId) =>
              copDroneTracks.get(droneId)?.callsign?.trim() || null
            }
            // `activeSelectedId`, not `selection.selectedDroneId`: the panel can
            // only draw an aircraft the site filter still admits, and the dock
            // verb must be offered exactly when there is somewhere to dock to.
            selectedDroneId={activeSelectedId}
            pickedDroneId={selection.selectedDroneId}
            canOperate={canDispatch}
            // Camera control (issue #244) — the same object FEED and the manual
            // stage get, so the three surfaces cannot disagree about what this
            // aircraft will accept. Applied only to the SELECTED aircraft's
            // windows; see CopCameraWindows.
            cameraControl={cameraControl && { ...cameraControl, flightKeysOwnKeyboard: manualActive, aircraftAim }}
            // So the dock verb can say where the picture will actually go: back
            // to the stage when the stage is drawing this aircraft and would
            // take this camera next, otherwise to the panel.
            stageDrawsPicture={stagePlacement !== null}
            selectedPoppedCameras={selectedPoppedCameras}
          />

          {/* The right column: one width, one sheet at a time, a tab strip to
              move between them without losing any sheet's state. `contents` on
              mobile keeps each sheet's own fixed-bottom drawer behavior — the
              wrapper only exists as a box on lg, where the tabs need a row
              above the sheet. At lg it FLOATS over the map's right edge and
              publishes its width as CopStageInsets.right; see CopSidePanel. */}
          {activeSheet && (
            <CopSidePanel
              floating={lgUp}
              // Names the region for a screen reader, and names it by what is
              // actually in it rather than "side panel".
              label={
                activeSheet === 'zones'
                  ? SHEET_TAB.zones
                  : activeSheet === 'missions'
                    ? 'Missions'
                    : activeSheet === 'dock'
                      ? `${sheetDockLabel ?? 'Dock'} dock`
                      : `${sheetDroneLabel ?? 'Aircraft'} details`
              }
            >
              <div className="hidden lg:block shrink-0">
                <CopSheetTabs
                  active={activeSheet}
                  droneLabel={sheetDroneLabel}
                  dockLabel={sheetDockLabel}
                  liveCount={queueLiveCount}
                  canAuthorZones={canDispatch}
                  onSelect={handleSelectSheetTab}
                />
              </div>
              <div className="contents lg:flex lg:flex-1 lg:min-h-0">
                {activeSheet === 'zones' ? (
                  /* Zones, the fourth tab (slice 8). It was a 300 px box floating
                     at `top-2 right-14` over the map - which the side panel now
                     covers outright, and which put the only zone-authoring
                     surface in the product on top of the picture it is drawn
                     against. In the column it takes the width the other sheets
                     take, and the map underneath stays whole. */
                  <ZonesPanel
                    geofences={copGeofences}
                    onSaveGeofence={handleSaveGeofence}
                    onDeleteGeofence={handleDeleteGeofence}
                    onZoomToGeofence={handleZoomToGeofence}
                    // Kind-scoped, which is what the old comment here claimed
                    // and the old code did not do: an operator who armed
                    // something else keeps it.
                    onClose={leaveZones}
                    drawMode={zoneShape}
                    onDrawModeChange={(shape) => {
                      // Arming REPLACES, so picking a shape stands down whatever
                      // else held the map. The geometry starts over either way.
                      dispatchZone({ type: 'RESET_DRAWING' });
                      if (shape) armMapMode({ kind: 'zone-draw', shape });
                      else disarmMapMode();
                    }}
                    drawPoints={zone.drawPoints}
                    polygonClosed={zone.polygonClosed}
                    onClosePolygon={() => dispatchZone({ type: 'CLOSE_POLYGON' })}
                    circleCenter={zone.circleCenter}
                    onSetCircleCenter={(lat, lng) => dispatchZone({ type: 'SET_CIRCLE_CENTER', lat, lng })}
                    circleRadius={zone.circleRadius}
                    onCircleRadiusChange={(radius) => dispatchZone({ type: 'SET_CIRCLE_RADIUS', radius })}
                    form={zoneForm}
                    dispatchForm={dispatchZoneForm}
                  />
                ) : activeSheet === 'missions' ? (
                  coverageAttachTarget ? (
                    /* THE ROTATION FOR A MISSION COMPOSE JUST MADE, in the slot compose was
                       occupying a moment ago. It outranks both editors because it is the tail of
                       the flow they start: compose has already stood down (`onCreated` closes it),
                       so nothing is being displaced. The station being configured is the last
                       waypoint of a route the map is still drawing behind this column - which is
                       true only because the hand-off selects the new mission and loads its
                       preview; compose's own draft route was cleared when it stood down. */
                    <CopCoverageAttachSheet
                      target={coverageAttachTarget}
                      drones={canvasRefData.drones}
                      dronesStatus={canvasRefData.loadError ? 'error' : canvasRefData.ready ? 'ready' : 'loading'}
                      onRetryDrones={loadComposeRefData}
                      capableTypes={coverageCapableTypes}
                      rotationStrategies={coverageRotationStrategies}
                      onClose={() => setCoverageAttachTarget(null)}
                      // Put it back where it was, on the COP, rather than sending the operator to
                      // the mission page. The target is handed back so a reopen cannot resurrect a
                      // different mission's attach than the one that was stood down.
                      //
                      // THE COLUMN HAS TO COME BACK TOO, not just the target (#685 review HIGH).
                      // This sheet renders only inside the `activeSheet === 'missions'` branch,
                      // and every unmount path the warning exists for - SELECT_DOCK, OPEN_DETAILS,
                      // SET_DETAILS_SEGMENT - sets `missionsPanelOpen: false`. The target was
                      // never cleared on those paths either, so restoring it alone was a
                      // same-reference no-op and Finish did visibly nothing.
                      //
                      // That made it dead in exactly the case it was written for: a NOW-lane
                      // launch where the operator taps the dock to watch `pre_takeoff`, gets the
                      // toast, presses Finish and gets no response, with the toast then expiring
                      // and no cue that the way back is the MISSIONS tab. The one path where it
                      // did work - the X or Escape - is the path that never needed it.
                      //
                      // THROUGH `openSheetFromOutside`, and the ref inside it is the point.
                      // Reading `zone.showPanel` directly here does not work: the toast's action
                      // closes over this callback as of the sheet's LAST COMMITTED render, and
                      // opening zones is precisely what unmounts the sheet - so the captured
                      // value is always the pre-zones `false`, `leaveZones()` never runs, and
                      // OPEN_MISSIONS_PANEL then loses the `activeSheet` precedence to zones.
                      // Finish would light up and change nothing, which is the same dead-control
                      // failure this whole action exists to fix, on the one path left.
                      // `openSheetFromOutside` reads `zoneShowPanelRef`, which is current at
                      // click time, and its own docstring describes this bug verbatim.
                      onReopen={(t) => openSheetFromOutside(() => {
                        dispatchSelection({ type: 'OPEN_MISSIONS_PANEL' });
                        setCoverageAttachTarget(t);
                      })}
                      onAttached={() => {
                        // The chip on the row and the block under it both read from the fleet-wide
                        // coverage poll, so without this the station an operator just configured
                        // looks unheld for up to 20 seconds.
                        refreshCoverage();
                        refreshMissionsQueueRef.current?.();
                      }}
                      width={dockSheetWidth}
                      onWidthChange={setDockSheetWidth}
                    />
                  ) : editing ? (
                    /* Revising a saved route. Like compose, it takes the missions
                       slot rather than covering the map — the route is being
                       changed ON the live picture. */
                    <CopMissionEdit
                      mission={editing}
                      route={editRoute}
                      armed={editArmed}
                      onArm={() => armMapMode({
                        kind: 'mission-edit',
                        missionId: editing.id,
                        // The saved mission's own type decides what a click collects.
                        shape: geometryShapeForType(editing.type),
                      })}
                      onDisarm={() => dispatchMapMode({ type: 'DISARM_KIND', kind: 'mission-edit' })}
                      onSaved={(missionId, saved) => {
                        setEditing((prev) =>
                          prev && prev.id === missionId
                            ? { ...prev, storedSignature: drawnRouteSignature(saved, prev.altitudeM) }
                            : prev,
                        );
                        refreshMissionsQueue();
                        loadMissions();
                      }}
                      onRevert={() => {
                        // Back to what the server holds — which is what the
                        // aircraft would fly. The stored route is re-read rather
                        // than reconstructed, so a revert cannot invent one.
                        void api
                          .get<{ data: MissionDetailRow }>(`/api/missions/${editing.id}`, {
                            signal: AbortSignal.timeout(10_000),
                          })
                          .then((res) => {
                            // The editor may have moved on while this was in
                            // flight — closed, or reopened on a DIFFERENT
                            // mission. `setEditing` already refused that case,
                            // but the route load did not, so a slow revert on A
                            // could drop A's waypoints into an editor showing B
                            // and a Save would then write them to B. Both halves
                            // answer to the same check now.
                            if (editingIdRef.current !== res.data.id) return;
                            const drawn = toDraftWaypoints(res.data.waypoints ?? []);
                            editRoute.load(drawn);
                            setEditing((prev) =>
                              prev && prev.id === res.data.id
                                ? { ...prev, storedSignature: drawnRouteSignature(drawn, prev.altitudeM) }
                                : prev,
                            );
                          })
                          .catch(() => toast.error('Failed to re-read the saved route'));
                      }}
                      onClose={closeEditRoute}
                      width={dockSheetWidth}
                      onWidthChange={setDockSheetWidth}
                    />
                  ) : composeOpen ? (
                    /* Compose takes the missions slot rather than covering the
                       map: the route is being drawn ON the live picture, so the
                       picture has to stay visible and interruptible while it is. */
                    <CopMissionCompose
                      route={composeRoute}
                      drones={canvasRefData.drones}
                      pilots={canvasRefData.pilots}
                      sites={canvasRefData.sites}
                      ready={canvasRefData.ready}
                      loadError={canvasRefData.loadError}
                      keepInFences={keepInFences}
                      onAltitudeChange={setComposeAltitudeM}
                      missionType={composeType}
                      onMissionTypeChange={handleComposeTypeChange}
                      onOrbitRadiusChange={setComposeOrbitRadiusM}
                      armed={composeArmed}
                      onArm={() => armMapMode({
                        kind: 'mission-compose',
                        // The type the operator has CHOSEN decides what a click collects, so
                        // re-arming is not a chance to re-run the type's route-capping side
                        // effect (`handleComposeTypeChange`) over points already placed.
                        shape: geometryShapeForType(composeType),
                      })}
                      onClose={closeCompose}
                      onCreated={closeComposeAndRefresh}
                      onCreatedWithoutId={closeComposeAndRefresh}
                      coverageCapableTypes={coverageCapableTypes}
                      onCoverageRequested={(request) => {
                        setCoverageAttachTarget(request);
                        /*
                          PUT THE STATION BACK ON THE MAP, which is the entire reason this sheet
                          is not a modal any more.

                          `closeCompose` clears the DRAFT route the operator just drew, and a
                          freshly created plan-lane mission is not `in_progress`, so it is not in
                          `activeMissions` either - `mapMissions` would draw nothing at all and the
                          operator would configure a station's radius against empty map. Selecting
                          it puts it in `mapMissions`, and the preview fetch is what gives that
                          entry its waypoints.

                          SELECTED, NOT FITTED. `handleSelectMission` would do both, but its fit
                          flies the camera - and on the NOW lane this runs at the instant the
                          aircraft is commanded off the pad. Yanking the map during golden-flow
                          steps 7 to 11 is the same defect in a different costume. The operator
                          drew this route seconds ago; the camera is already looking at it.
                        */
                        setSelectedMissionId(request.missionId);
                        loadDraftPreview(request.missionId, false);
                      }}
                      onOpenMission={(missionId) => {
                        closeCompose();
                        router.push(missionDetailHref(missionId));
                      }}
                      width={dockSheetWidth}
                      onWidthChange={setDockSheetWidth}
                    />
                  ) : (
                  <CopMissionsPanel
                    coverageByMission={coverageByMission}
                    coverageDegraded={coverageDegraded}
                    rotation={rotation}
                    rotationTimeline={rotationTimeline}
                    rotationDegraded={rotationDegraded}
                    rotationLastReadAt={rotationLastReadAt}
                    /*
                      BOTH READS, and the missions queue with them.

                      A control moves the coverage ROW as well as the forecast, and the row is what
                      `useCopCoverage`'s 20 s fleet poll owns - so refreshing only the rotation left
                      the badge, the SUSPEND/RESUME label and `missionCoverageLock` stale for up to
                      twenty seconds after the operator's own press. Detach was worst: the server
                      emits no `coverage_event` for it, so the block kept offering SUSPEND and
                      DETACH against a row that no longer existed, and STOP stayed disabled on the
                      mission under a sentence telling the operator to go and detach it - which is
                      exactly what they had just done. The attach path in this same file already
                      refreshed both; this was the other half of the pair.
                    */
                    onRotationRetry={() => {
                      refreshRotation();
                      refreshCoverage();
                      refreshMissionsQueueRef.current?.();
                    }}
                    reliefLinkOf={reliefLinkOf}
                    onEditRoute={startEditRoute}
                    onStop={handleStopMission}
                    stoppingId={stoppingMissionId}
                    returningMissionIds={returningMissionIds}
                    missions={missionsQueue.missions}
                    loading={missionsQueue.loading}
                    loadError={missionsQueue.loadError}
                    everLoaded={missionsQueue.everLoaded}
                    coverageUnread={missionsQueue.coverageUnread}
                    canDispatch={canDispatch}
                    selectedMissionId={selectedMissionId}
                    droneTracks={copDroneTracks}
                    onCenterOnDrone={handleCenterOnDrone}
                    onSelectMission={handleSelectMission}
                    onHoverMission={setHoveredMissionId}
                    onLaunched={(droneId, missionId) => {
                      handleMissionLaunched(droneId, missionId);
                      refreshMissionsQueue();
                    }}
                    onNewMission={startCompose}
                    onRetry={refreshMissionsQueue}
                    onClose={() => dispatchSelection({ type: 'CLOSE_MISSIONS_PANEL' })}
                    width={dockSheetWidth}
                    onWidthChange={setDockSheetWidth}
                  />
                  )
                ) : activeSheet === 'dock' && selection.selectedDockId ? (
                  <CopDockSheet
                    data={selectedDockStatus}
                    dockId={selection.selectedDockId}
                    dockCallsign={selectedDockStatus?.callsign ?? selectedRegistryDock?.callsign ?? null}
                    registryStatus={selectedRegistryDock?.status ?? 'awaiting_connection'}
                    registryIsOnline={selectedRegistryDock?.is_online ?? false}
                    capabilities={selectedRegistryDock?.capabilities ?? []}
                    positionCheck={selectedRegistryDock?.position_check}
                    bays={dockBays}
                    baysError={dockBaysError}
                    operationalPhase={operationalPhase}
                    airborneDrones={airborneDrones}
                    tagLandings={tagLandings}
                    selectedBayDroneId={selection.selectedBayDroneId}
                    onSelectBayDrone={handleSelectBayDrone}
                    onLaunch={handleLaunch}
                    onGoToDrone={handleCenterOnDrone}
                    subscribeDockAck={subscribeDockAck}
                    onClose={() => dispatchSelection({ type: 'CLOSE_DOCK' })}
                    openCameras={openDockCameras}
                    onOpenCamera={openDockCamera}
                    width={dockSheetWidth}
                    onWidthChange={setDockSheetWidth}
                  />
                ) : selectedDrone ? (
                  <DroneWorkspace
                    drone={selectedDrone}
                    // The one reading that says whether a silent aircraft is
                    // powered off or merely has our app dead on the RC. Null for
                    // MAVLink and RemoteID aircraft, which grow no companion row.
                    companion={selectedDroneCompanion}
                    registryRow={selectedDroneRow}
                    homeDock={selectedDroneHomeDock}
                    mission={selectedDroneMission}
                    missionsUnavailable={missionsCountFailed}
                    alerts={alerts}
                    maintenance={selectedDroneMaintenance}
                    onAcknowledgeAlert={canAckResolve ? handleAcknowledge : undefined}
                    onResolveAlert={canAckResolve ? handleResolve : undefined}
                    // Null when the airframe does not report progress. mission_current
                    // is MAVLink-only — no DJI, Autel or companion aircraft reports it
                    // — so coercing "not reported" to zero would claim a flying
                    // aircraft has reached no waypoint at all. The panel renders that
                    // as unknown, never as zero.
                    missionWaypointIndex={
                      activeWaypointIndex(
                        selectedDrone.mission_current,
                        selectedDroneMission?.waypoints?.length ?? 0,
                      )
                    }
                    segment={selection.detailsSegment}
                    onSegmentChange={(segment) =>
                      dispatchSelection({ type: 'SET_DETAILS_SEGMENT', segment })
                    }
                    // Through `openCameraWindow` rather than straight to the
                    // reducer, so an open past the cap is refused out loud
                    // instead of being a key that does nothing.
                    onPopOutVideo={(cameraId) =>
                      openCameraWindow({
                        kind: 'drone',
                        droneId: selectedDrone.drone_id,
                        cameraId: cameraId ?? SINGLE_CAMERA,
                        // Snapshotted at the press: the live list is
                        // site-filtered, so an aircraft can leave it while its
                        // window is still playing.
                        callsign: selectedDrone.callsign?.trim() || null,
                        // Pressing a key that names this camera is the operator
                        // asking for this picture by name, so the next aircraft
                        // they click on the map does not take it away.
                        pinned: true,
                      })
                    }
                    onDockVideo={(cameraId) =>
                      dispatchSelection({
                        type: 'DOCK_VIDEO',
                        droneId: selectedDrone.drone_id,
                        // Normalised, like the pop-out path beside it. A stream
                        // row with a null `camera_id` passes undefined, which
                        // the reducer reads as the wildcard "dock every camera
                        // of this aircraft" - so docking the unnamed feed of a
                        // two-camera airframe closed the other one's window too.
                        cameraId: cameraId ?? SINGLE_CAMERA,
                      })
                    }
                    poppedCameras={selectedPoppedCameras}
                    // Where the stage is drawing this aircraft's picture (a manual
                    // session, or the split beside the map), so FEED reports that
                    // placement rather than offering to open a second player.
                    stagePlacement={stagePlacement}
                    // The health dot's picture-level half. The panel draws no
                    // player of its own now, so the verdict has to arrive from
                    // whichever window is showing this aircraft.
                    playerStates={videoPlayerStates}
                    onClose={() => dispatchSelection({ type: 'CLOSE_DETAILS' })}
                    collapsed={selection.detailsCollapsed}
                    onToggleCollapsed={() =>
                      dispatchSelection({ type: 'TOGGLE_DETAILS_COLLAPSED' })
                    }
                    width={dockSheetWidth}
                    onWidthChange={setDockSheetWidth}
                  />
                ) : null}
              </div>
            </CopSidePanel>
          )}
        </div>

        {canDispatch && (
          <QuickDispatchDialog
            open={quickDispatch.dispatchOpen}
            onOpenChange={handleDispatchDialogOpenChange}
            drones={dispatchDroneOptions}
            fleetUnavailable={droneRegistryFailed}
            fleetLoading={droneRegistryLoading}
            injectedCoordinates={quickDispatch.injectedCoords}
            injectedDescription={quickDispatch.injectedDescription}
            altitudeProfile={altitude.profile}
            ceilingAuthority={altitude.authority}
            pickTargetActive={mapMode.kind === 'dispatch-pick'}
            onPickTargetChange={(active) => {
              if (active) armMapMode({ kind: 'dispatch-pick' });
              else dispatchMapMode({ type: 'DISARM_KIND', kind: 'dispatch-pick' });
            }}
            onDispatched={handleDispatchedFromDialog}
          />
        )}

      </div>
    </div>
    </FeedAnalyticsProvider>
    </StageInsetsProvider>
  );
}

export default function CopPage() {
  return (
    <Suspense fallback={null}>
      <CopView />
    </Suspense>
  );
}
